import { z } from 'zod';
import { AXOrder } from '../asyncDesign/types';
import {
  PayColumnInvoices,
  OrgContextState,
  ExpressSettings,
  MAX_ORGANIZATION_NAME_LENGTH
} from './organization';
import { AffinityXOrderNumber } from '../integrations/affinityx/types';
import {
  EPayoutData,
  EStripeBankAccount,
  EStripePaperCheck,
  EStripePaymentMethodCard,
  StripeBankAccountSchema,
  StripePaymentMethodUSBankAccountSchema
} from './stripe';
import {
  ProductSiteSettingUpdateRequest,
  ProductSiteSettingCreateResponse,
  ProductSiteSettingCreateRequest
} from './productSiteSetting';
import {
  BulkDownloadRequest,
  BulkDownloadResponse,
  ECache,
  EOrganization,
  EPublicNotice,
  ERate,
  ERef,
  EStripePaymentMethodUSBankAccount,
  EStripePayout,
  ESubscription,
  ETemplateStyles
} from '.';
import {
  EventInvoiceData,
  LoadPayInvoiceResponse,
  MarkUncollectibleOutcome,
  PaymentMethods
} from './invoices';
import { DisplaySiteSubdomain } from './displaySite';
import { PayoutGroup, PayoutParentRowData } from './payout';
import {
  ElasticCustomerSearchResults,
  ElasticDocumentType,
  ElasticPublicNoticeSearchResults,
  SearchableCustomerRecordFilter,
  SearchableNoticeRecord,
  SearchableNoticeRecordFacet,
  SearchableNoticeRecordFilter,
  SearchableOrganizationRecord,
  SearchableOrganizationRecordFilter,
  SearchableInvoiceRecordFilter,
  SearchablePublicNoticeRecordFacet,
  SearchablePublicNoticeRecordFilter,
  SORT_DIRECTIONS,
  ElasticOrderSearchResults,
  SearchableNewspaperOrderRecordFilter,
  ElasticOrderFacetsResults,
  SearchableNewspaperOrderRecordFacet,
  SearchableNewspaperOrderRecord,
  SearchableAdjudicationAreaRecordFilter,
  SearchableAdjudicationAreaRecord,
  SearchableInvoiceRecordWithCheck,
  SearchableInvoiceRecord,
  SearchablePublicationIssueSectionRecordFilter,
  SearchablePublicationIssueSectionRecord,
  SearchableExpressEmailConversationRecord
} from './searchable';
import { EUser } from './user';
// NOTE: Do not use the `ResponseOrError` type directly as a Response type.
// Instead, use the `ApiResponseOrError` type, which is designed to preserve
// the error message over the wire (use `sendResponseOrError` to easily
// convert a `ResponseOrError` to an `ApiResponseOrError`).
import { ApiResponseOrError, SearchedOrganizations } from './responses';
import {
  BriefBatch,
  BriefBatchAdminView,
  BriefBatchCadence,
  BriefBatchPaginatedPdf
} from './briefBatch';
import { RunForVerification, RunNeedingVerification } from './runs';
import {
  ColumnPaymentResponse,
  InvoiceTransactionsCreateAuthorizationRequest,
  InvoiceTransactionsCreateChargeRequest,
  InvoiceTransactionsCreateSessionRequest,
  InvoiceTransactionsCreateSessionResponse,
  InvoiceTransactionsFetchRefundTransactionsResponse,
  PaymentGatewayResult
} from './invoiceTransaction';
import {
  ImplementationStatus,
  OrganizationType,
  Product,
  TimezoneType
} from '../enums';
import { IntegrationCustomerInfo } from './integrations/request';
import {
  ORDER_CANCELLED,
  ORDER_EDITED,
  ORDER_INVOICE_CREATED,
  ORDER_INVOICE_PAID,
  ORDER_INVOICE_PAID_OUT_OF_PLATFORM,
  ORDER_INVOICE_REFUNDED,
  ORDER_MANUAL_SYNC_REQUEST,
  ORDER_NEWSPAPER_ORDER_AT_DEADLINE,
  ORDER_NEWSPAPER_ORDER_CONFIRMED,
  ORDER_NEWSPAPER_ORDER_DELETED,
  SyncTriggerEvent
} from './events';
import { RecordPreservationSettings } from './mergeOrganizations/recordPreservationSettings';
import { IgnoreCustomSettings } from './mergeOrganizations/ignoreCustomSettings';
import { FileType } from './mime';
import { DocumentProperties } from './documents';
import { RegenDocType } from './documents/regeneration';
import { UploadMethod } from '../affidavits';
import { AutomatedAffidavitSignerValue } from '../enums/AutomatedAffidavitSigners';
import { AutomatedAffidavitNotaryValue } from '../enums/AutomatedAffidavitNotaries';
import { SigninProviderType } from '../enums/SigninProviderType';
import {
  AutomatedAffidavitNotarizationBatch,
  DigitalSigNotarizationNotice,
  FastWetSigNotarizationNotice,
  ManualVerificationRunData,
  WetSigNotarizationNotice
} from '../affidavits/types';
import { AdminDocumentUpdateRequest } from './admin';
import { OrderCreateRequest, OrderCreateResponse } from './order';
import { AffidavitData } from './documents/affidavits';
import { StorageReference } from './firebase';
import { AffidavitTemplateV2 } from './affidavitTemplate';
import { AutomatedAffidavitSigner } from './automatedAffidavitSigner';
import { ProofNotaryObject } from '../affidavits/proof';
import { VisionDataBotResponse } from './rpa';
import { OrganizationTypeValue } from '../enums/OrganizationType';
import { DisplayAdEmailNotification } from '../displayAds';
import { SerializedAssignNoticesItem } from './affidavits/wetSignature';
import { NotarizationStatus } from './notarization';
import { EEditionStatus, EEditionUploadMethod } from './eedition';
import { SerializedOrganizationModel } from '../model/objects/organizationModel';
import { SerializedPublicationIssueModel } from '../model/objects/publicationIssueModel';
import { SearchSubdomainsRequest, SubdomainQueryResponse } from './subdomain';
import { OrderDisplayNewspaperResponse } from './orderDisplay';
import { PublishingMedium } from '../enums/PublishingMedium';
import {
  OrderCouponApply,
  OrderCouponRemove,
  CouponSaveRequest
} from './coupon';
import {
  FullNoticePaginationSubmissionAudit,
  FullOrderPaginationSubmissionAudit
} from './pagination';
import { NewspaperOrderStatus } from './newspaperOrder';
import { ExpressPlacementBilling } from './expressPlacementBilling';
import { AutomaticBulkDownloadRequest } from './requests';
import {
  EditablePublisherPaginationSettingsSchema,
  OptimizerHyperparams
} from './publisherPaginationSettings';
import {
  ReportsPaginationReportRow,
  ReportsPubIssuesTableDeadline
} from './reports/PaginationReportsRows';
import { CustomersTableRow } from './reports/CustomerTableRows';
import {
  ProcessedProductsCategoryData,
  ProductRevenueByMonthChartData
} from './reports/ProductsTable';
import { AccountingTableRowData } from './reports/AccountingTableRows';
import { IntegrationFormat, IntegrationSettings } from './integrationSettings';
import { AdminPaymentsReconciledChecksRequest } from './paperCheck';
import { ApiClient, ApiClientPaymentMethodType } from './apiClient';

export const HEADER_SESSION_ID_KEY = 'x-column-sessionid';
export const HEADER_COLUMN_APP = 'x-column-app';

type GenericResponse = {
  success?: boolean;
  error?: string;
};

type AdminInvoiceRegenerationResponse = GenericResponse & {
  message?: string;
};

export type AdminSubscriptionsInvoiceItemsReportItem = {
  id: string;
  description: string;
  type?: number;
  /** Amount in cents */
  amount: number | null;
  /** Firestore notice/ad document ID */
  orderId: string | null;
  /** Firestore newspaper order document ID */
  newspaperOrderId: string | null;
  invoiceId: string | null;
  createdIso: string | null;
};

export type AdminSubscriptionsInvoiceItemsReportResponse = {
  stripeInvoiceId: string;
  stripeSubscriptionId: string;
  items: AdminSubscriptionsInvoiceItemsReportItem[];
};

export type AdminCreateNoticeTransferResponse = {
  /**
   * Same as the `transfer` prop on ENotice but instead of a transfer ref, we use a string for the ID
   */
  transfer: boolean | string | null | undefined;
  reasonForNoTransfer?: string;
};

type PasswordResetEmailResponse = GenericResponse & {
  noUserFound: boolean;
};

type AffidavitAutomationAssignNoticesToMailCentersResponse =
  | {
      success: true;
    }
  | {
      success: false;
      error: string;
    };

export type RelatedPaperOption = {
  label: string;
  value: string;
  state: number;
};

type GetRelatedPapersResponse = ApiResponseOrError<{
  subdomainPaper: RelatedPaperOption | null;
  publisherActivePaper: RelatedPaperOption | null;
  relatedPapers: RelatedPaperOption[];
}>;

type AdminOrganizationsSubdomainSettingsResponse = ApiResponseOrError<null>;

type AffidavitAutomationGetNoticesResponse = {
  success?: boolean;
  /**
   * @deprecated TODO: remove once we deprecate the affidavits Retool page
   */
  noticeIds: string[];
  notarizationNotices: DigitalSigNotarizationNotice[];
};

type AffidavitAutomationGetRunsToVerifyResponse = {
  runsToVerify: ManualVerificationRunData[];
};

type AffidavitAutomationGetFullWetSigNotarizationNoticesResponse =
  | {
      success: true;
      notarizationNotices: WetSigNotarizationNotice[];
    }
  | {
      success: false;
      error: string;
    };

type AffidavitAutomationGetNoticesForWetSigNotarizationResponse =
  | {
      success: true;
      notarizationNotices: FastWetSigNotarizationNotice[];
    }
  | {
      success: false;
      error: string;
    };

type AffidavitAutomationGetNoticesAssignedToMailCentersResponse =
  | {
      success: true;
      notarizationNotices: (
        | WetSigNotarizationNotice
        | FastWetSigNotarizationNotice
      )[];
    }
  | {
      success: false;
      error: string;
    };

type AffidavitAutomationAddToNotarizationResponse =
  | {
      success: true;
      partialErrors?: string[];
      notarizationId: string;
    }
  | {
      success: false;
      error: string;
    };

type AffidavitAutomationFinalizeResponse =
  | { success: true }
  | { success: false; error: any };

type AffidavitAutomationGetAllNotariesResponse = ApiResponseOrError<
  ProofNotaryObject[]
>;

type PaymentsDwollaPayoutDataResponse =
  | { success: true; payouts: PayoutGroup[] }
  | { success: false; error?: string };

type PaymentsListInvoicePaymentMethodsResponse = ApiResponseOrError<{
  cards: EStripePaymentMethodCard[];
  bank_accounts_sources: EStripeBankAccount[];
  bank_accounts_payment_methods: EStripePaymentMethodUSBankAccount[];
}>;

type PaymentsStripeCreateSetupIntentResponse =
  | { success: true; clientSecret: string }
  | { success: false; error?: string };

type PaymentsGetBankAccountPaymentMethodStatusResponse =
  | { success: true; status: string }
  | { success: false; error?: string };

type PaymentsGetClientSecretForSetupIntentResponse =
  | { success: true; setupIntentClientSecret: string }
  | { success: false; error?: string };

type PaymentsStripeCustomerDetailsResponse =
  | { success: true; name: string; email: string }
  | { success: false; error?: string };

type SearchCustomersResponse =
  | ({
      success: true;
    } & ElasticCustomerSearchResults)
  | {
      success: false;
      error: Error;
    };

type TemplatesForkResponse =
  | { success: true; downloadUrl: string }
  | { success: false };

type TemplatesForkCleanResponse =
  | { success: true; idmlBuffer: string }
  | { success: false; error?: string };

type TemplatesStylesResponse =
  | { success: true; styles: ETemplateStyles }
  | { success: false; error?: string };

type UsersCreateAdvertiserResponse =
  | {
      success: true;
      organizationId: string;
    }
  | { success: false; error: string };

type RetoolCreateAdvertiserOrgResponse = UsersCreateAdvertiserResponse;

type RatesGenerateCodeResponse =
  | { success: true; code: number }
  | { success: false; error: string };

type PaywayQueueTransactionResponse = {
  paywayCode: string;
  paywayMessage: string;
  columnPaymentSuccess?: boolean;
  errorMessage?: string;
};

type IntegrationsCustomersFindResponse =
  | {
      success: true;
      customer: IntegrationCustomerInfo;
    }
  | {
      success: false;
      error: string;
    };

export type UserQueryResponse = Pick<EUser, 'name' | 'email' | 'occupation'> & {
  userId: string;
  organizationID: string;
  activeOrganizationID: string;
  allowedOrganizationIDs: string[];
};

type SearchAdvertiserOrganizationsResponse =
  | {
      success: true;
      organizationsList: SearchedOrganizations[];
    }
  | {
      success: false;
    };

type SearchOrdersResponse =
  | ({
      success: true;
    } & ElasticOrderSearchResults)
  | {
      success: false;
      error: Error;
    };

type SearchOrganizationsResponse = {
  results: SearchableOrganizationRecord[];
  page: number;
  error?: boolean;
};

type SearchInvoicesResponse =
  | { error: true }
  | {
      results: SearchableInvoiceRecord[];
      page: {
        current: number;
        total_pages: number;
        total_results: number;
        size: number;
      };
      error: false;
    };

type SearchPublicNoticesResponse =
  | (ElasticPublicNoticeSearchResults & { success: true })
  | {
      error: boolean;
      success: false;
    };

type SearchPublicNoticesFacetsResponse =
  | {
      facets: (string | number)[];
      success: true;
    }
  | {
      error: boolean;
      success: false;
    };

type SearchUserNoticesResponse = {
  results: SearchableNoticeRecord[];
  page: number;
  error?: boolean;
};
type UsersAuthProvidersByEmailResponse =
  | { success: true; numAuthProviders: number }
  | { success: false; error: string };

type IntegrationsVisionDataOcrResponseLocation = {
  type: 'text-location';
  location: {
    upperLeftCorner: {
      x: number;
      y: number;
    };
    height: number;
    width: number;
  };
};
type IntegrationsVisionDataOcrResponseExtraction = {
  type: 'text-extract';
  value: string;
};

type UserRegisterFailureResponse = {
  success: false;
  type:
    | 'invalid user input'
    | 'user already exists with non-temporary password'
    | 'new user has temporary password'
    | 'unknown';
  error: Error;
};
type UserRegisterSuccessResponse = {
  success: true;
  token: string;
  userId: string;
  wasUserAnonymous: boolean;
};
type UsersRegisterResponse =
  | UserRegisterFailureResponse
  | UserRegisterSuccessResponse;

export type IntegrationsVisionDataOcrResponse =
  | IntegrationsVisionDataOcrResponseExtraction
  | IntegrationsVisionDataOcrResponseLocation;

type IntegrationsTriggerEventResponse =
  | { success: true; eventId: string }
  | { success: false; error?: string };

type ExistsResponse = {
  exists: boolean;
};
type UsersGetUserByEmailResponse =
  | {
      success: true;
      userId: string;
      data: Pick<EUser, 'hasTemporaryPassword'>;
    }
  | { success: false; error?: string };

type UsersGetUsersFromCustomerOrgResponse =
  | { success: true; userIds: string[] }
  | { success: false; error?: string };

type AdminSignInResponse =
  | { success: true; email: string; firebaseToken: string }
  | { success: false };

type GoogleMicrosoftSignInResponse =
  | { success: true; firebaseToken: string }
  | { success: false };

type DocumentsPropertiesResponse =
  | { success: true; properties: DocumentProperties }
  | { success: false; error?: string };

type AdminSyncToElasticResponse =
  | { success: true }
  | { success: false; error: string };

type AdminPaginationManifestResponse =
  | { success: true; manifestUrl: string }
  | { success: false; error?: string };

type OrderFilerNameResponse = ApiResponseOrError<{
  isAnonymousOrder: boolean;
  filerFirstName: string;
  filerLastName: string;
}>;

type NoticePaginationReviewResponse =
  ApiResponseOrError<FullNoticePaginationSubmissionAudit>;
type OrderPaginationReviewResponse =
  ApiResponseOrError<FullOrderPaginationSubmissionAudit>;

type PaginationDownloadResponse =
  | { success: true; url: string | undefined }
  | { success: false; error: string };

type PaginationReadyOrdersResponse = ApiResponseOrError<
  SearchableNewspaperOrderRecord[]
>;

type GetPublicationIssueOrdersResponse =
  | { success: true; orders: SearchableNewspaperOrderRecord[] }
  | { success: false; error: string };

type PaginatorsResponse = ApiResponseOrError<{
  paginators: { id: string; name: string; userColor?: string }[];
}>;

type AdminExternalUploadResponse = {
  dates: { value: string; success: boolean; totalNotices: number }[];
};

type AdminUserManagementSearchResponse = {
  users: UserQueryResponse[];
};

type AdminImpersonateUserResponse = {
  token: string;
};

type OrdersGrantAccessResponse =
  | { success: true; token: string; orderId: string }
  | { success: false; error: string };

type AdPriceResponse =
  | { success: true; price: number }
  | { success: false; error: string };

type PDFDownloadResponse = ApiResponseOrError<{ url: string }>;

type DocumentsAffidavitsPreviewResponse = ApiResponseOrError<StorageReference>;

/**
 * The proofDownload can either be the contents of a file or a URL.
 * If the value is a Buffer, it represents the actual file contents that need to be downloaded.
 * If the value is a string, it represents a URL from which the file can be downloaded.
 * This distinction is important because a string here does not indicate a plaintext file, but rather a URL.
 */
export type OrdersDownloadProofsData = {
  fileNameWithoutExtension: string;
  proofDownload: string | Buffer | null;
  fileType: FileType;
};

type GetOrCreateCustomerWithCustomerOrganizationResponse =
  | { success: true; customerId: string }
  | { success: false; error: string };

export type DisplayQAMessage = {
  id: string;
  label: string;
  description: string;
  level: 'warning' | 'error' | 'success';
};
export type DisplayQAResult = {
  success: boolean;
  messages: DisplayQAMessage[];
};

export type EEditionTrackerRowData = {
  publicationIssue: SerializedPublicationIssueModel;
  publisher: SerializedOrganizationModel;
  parent: SerializedOrganizationModel | null;
};
type AdminAffidavitsGetEEditionTrackerDataResponse = ApiResponseOrError<
  EEditionTrackerRowData[]
>;

type SearchAdjudicationAreaResponse = {
  results: SearchableAdjudicationAreaRecord[];
  page: {
    current: number;
    total_pages: number;
    total_results: number;
    size: number;
  };
};

export const AutoLayoutRequestElementSchema = z.object({
  id: z.string(),
  order_type: z.string(),
  variants: z.array(
    z.object({
      width: z.number(),
      height: z.number(),
      columns: z.number()
    })
  )
});
export type AutoLayoutRequestElement = z.infer<
  typeof AutoLayoutRequestElementSchema
>;

export const LayoutBlockFixedElementSchema = z.object({
  id: z.string(),
  height: z.number(),
  width: z.number(),
  x: z.number(),
  y: z.number()
});
export type LayoutBlockFixedElement = z.infer<
  typeof LayoutBlockFixedElementSchema
>;

export const LayoutBlockOptionalElementSchema = z.object({
  id: z.string(),
  width: z.number(),
  height: z.number(),
  columns: z.number()
});

export const AutoPaginationRequestLayoutSchema = z.object({
  width: z.number(),
  height: z.number(),
  id: z.string(),
  max_whitespace: z.number(),
  fixed_elements: z.array(LayoutBlockFixedElementSchema),
  optional_elements: z.array(LayoutBlockOptionalElementSchema)
});
export type AutoPaginationRequestLayout = z.infer<
  typeof AutoPaginationRequestLayoutSchema
>;

export const AutoPaginationRequestLayoutBlockSchema = z.object({
  elements: z.array(AutoLayoutRequestElementSchema),
  layout: AutoPaginationRequestLayoutSchema,
  whitespace: z.number(),
  fixed_elements: z.array(LayoutBlockFixedElementSchema)
});

const AutoLayoutResponseElementSchema = z.object({
  id: z.string(),
  width: z.number(),
  height: z.number(),
  x: z.number(),
  y: z.number(),
  bordered: z
    .object({
      thickness: z.number()
    })
    .optional()
});
export type AutoLayoutResponseElement = z.infer<
  typeof AutoLayoutResponseElementSchema
>;

export const AutoPaginationResponseBlockSchema = z.object({
  elements: z.array(AutoLayoutResponseElementSchema),
  layout: AutoPaginationRequestLayoutSchema,
  whitespace: z.number(),
  bordered: z
    .object({
      thickness: z.number()
    })
    .optional()
});

export const OptimizerResponseSchema = z.array(
  AutoPaginationResponseBlockSchema
);
export type AutoPaginationResponseBlock = z.infer<
  typeof AutoPaginationResponseBlockSchema
>;
export type OptimizerResponse = z.infer<typeof OptimizerResponseSchema>;

export const AutoPaginationResultsSchema = z.array(
  z.object({
    block: AutoPaginationResponseBlockSchema,
    url: z.string()
  })
);
export type AutoPaginationResults = z.infer<typeof AutoPaginationResultsSchema>;

export const AdminAutoPaginationUpdateSettingsRequestSchema = z.object({
  autoPaginationSettingsId: z.string().nonempty(),
  settings: EditablePublisherPaginationSettingsSchema.partial()
});
export type AdminAutoPaginationUpdateSettingsRequest = z.infer<
  typeof AdminAutoPaginationUpdateSettingsRequestSchema
>;

export const AdminAutoPaginationCreateDefaultSettingsRequestSchema = z.object({
  organizationId: z.string().nonempty()
});
export type AdminAutoPaginationCreateDefaultSettingsRequest = z.infer<
  typeof AdminAutoPaginationCreateDefaultSettingsRequestSchema
>;

export const AdminAutoPaginationTestRequestSchema = z.object({
  publicationIssueModelID: z.string().nonempty(),
  overrideExistingBlocks: z.boolean()
});
export type AdminAutoPaginationTestRequest = z.infer<
  typeof AdminAutoPaginationTestRequestSchema
>;

export const ExperimentDraftCompletionResponseSchema = z.object({
  content: z.string()
});

export const InboxAutomationTemplateResponseSchema = z.object({
  client: z.string(),
  state: z.string(),
  templateContent: z.string()
});
export type InboxAutomationTemplateResponse = z.infer<
  typeof InboxAutomationTemplateResponseSchema
>;

export const StripeCustomerCreateResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  stripeId: z.string()
});
export type StripeCustomerCreateResponse = z.infer<
  typeof StripeCustomerCreateResponseSchema
>;

export const VoidResponseSchema = z.void();
export type VoidResponse = z.infer<typeof VoidResponseSchema>;

/**
 * Response types for our API endpoints.
 */
export type EResponseTypes = {
  'admin/integrations/order-export-settings': ApiResponseOrError<void>;
  'pagination/test-auto-pagination': ApiResponseOrError<
    {
      block: AutoPaginationResponseBlock;
      url: string;
    }[]
  >;
  'admin/organizations/load-new-aging-data': ApiResponseOrError<{
    success: boolean;
  }>;
  'admin/organizations/set-report-url': ApiResponseOrError<{
    success: boolean;
  }>;
  'admin/organizations/clone-publisher': ApiResponseOrError<{
    success: boolean;
  }>;
  'admin/notices/display-site-notice/edit': ApiResponseOrError<void>;
  'admin/notices/edit-price-settings': ApiResponseOrError<void>;
  'admin/notices/sync-to-public-notice-site': ApiResponseOrError<void>;
  'admin/organizations/bulk-refresh/notices': ApiResponseOrError<void>;
  'admin/organizations/layout-settings/update': ApiResponseOrError<void>;
  'admin/display-site-uploads/add-display-site-upload-id': ApiResponseOrError<void>;
  'admin/display-site-uploads/delete-display-site-upload-id': ApiResponseOrError<void>;
  'admin/display-site-uploads/update-display-site-upload-id': ApiResponseOrError<void>;
  'admin/display-site-uploads/generate-feed-key': ApiResponseOrError<string>;

  'admin/organizations/display-sites/update': ApiResponseOrError<void>;
  'admin/affidavits/organizations/:organizationId/update-edition-portal-url': ApiResponseOrError<void>;
  'admin/notices/:noticeId/force-archive': ApiResponseOrError<void>;
  'admin/notices/:noticeId/change-filer': ApiResponseOrError<VoidResponse>;
  'admin/notices/:noticeId/cancel-invoice': ApiResponseOrError<VoidResponse>;
  'admin/organizations/refresh-affidavit-settings': ApiResponseOrError<void>;
  'search/publication-issue-sections': ApiResponseOrError<{
    results: SearchablePublicationIssueSectionRecord[];
    page: {
      current: number;
      total_pages: number;
      total_results: number;
      size: number;
    };
  }>;
  'docs/oauth': ApiResponseOrError<{ redirectUrl: string }>;
  'conversation-requests/search': ApiResponseOrError<
    SearchableExpressEmailConversationRecord[]
  >;
  'conversation-requests/refresh': ApiResponseOrError<void>;
  'archive/parse-twp': ApiResponseOrError<{
    notices: number;
    file: string;
    content: string;
  }>;
  'admin/organizations/duplicate-publisher': ApiResponseOrError<{
    newPublisherId: string;
  }>;
  'admin/customer-organizations/:customerOrganizationId/update': ApiResponseOrError<void>;
  'admin/briefs/': ApiResponseOrError<BriefBatchAdminView[]>;
  'admin/run-verification/': ApiResponseOrError<RunNeedingVerification[]>;
  'admin/run-verification/:runId': ApiResponseOrError<RunForVerification>;
  'admin/briefs/run': ApiResponseOrError<BriefBatch>;
  'admin/briefs/pdfs': ApiResponseOrError<{
    uploadId: string;
    eventId: string;
    paginatedPdfs: BriefBatchPaginatedPdf[];
  }>;
  // Server builds the zip, uploads it to GCS, and returns a CDN URL the
  // client follows to actually download the bytes — no auth headers
  // needed on the URL itself. `missingNewspapers` is the slugs of any
  // per-newspaper files that couldn't be added (e.g. transient GCS
  // errors) so the client can warn the admin that the zip is partial.
  'admin/briefs/:source/:date/zip': ApiResponseOrError<{
    url: string;
    filename: string;
    missingNewspapers: string[];
  }>;
  'admin/rates/:rateId/update': ApiResponseOrError<void>;
  'openapi/': any;
  'admin/affidavits/scrape-edition-run-date': ApiResponseOrError<{
    success: boolean;
    editionPDFUrl: string;
  }>;
  'admin/organizations/parent-org-settings': ApiResponseOrError<{
    success: boolean;
  }>;
  'admin/organizations/express-settings': ApiResponseOrError<{
    success: boolean;
  }>;
  'health/': { healthy: boolean };
  'admin/organizations/advertiser-express-settings': ApiResponseOrError<{
    success: boolean;
  }>;
  'admin/affidavits/notices/refresh-ars-from-publisher': ApiResponseOrError<{
    success: boolean;
  }>;
  'users/update-role': ApiResponseOrError<void>;
  'users/requests/accept': ApiResponseOrError<void>;
  'users/requests/decline': ApiResponseOrError<void>;
  'users/requests/snooze': ApiResponseOrError<void>;
  'admin/orders/update-newspaper-order': ApiResponseOrError<{
    success: boolean;
  }>;
  'orders/download-rtf': ApiResponseOrError<string>;
  'admin/orders/refresh-all-proofs': ApiResponseOrError<{ success: boolean }>;
  'admin/orders/refresh-proof': ApiResponseOrError<{ success: boolean }>;
  'admin/orders/update-ad-content': ApiResponseOrError<{ success: boolean }>;
  'admin/orders/rebuild-newspaper-auth': ApiResponseOrError<{
    success: boolean;
  }>;
  'admin/ad-building/orders/group-ads-by-type': ApiResponseOrError<
    Record<string, { filingTypeName: string; publicationGroup: string }>
  >;
  'admin/ad-building/orders/list-display-ads': ApiResponseOrError<{
    allFiles: {
      name: string;
      parentFolderId: string;
    }[];
    allFolders: {
      name: string;
      parentFolderId: string;
    }[];
  }>;
  'admin/ad-building/orders/download-block': ApiResponseOrError<{
    url: string;
  }>;
  'pagination/universal-download': ApiResponseOrError<{
    downloadUrl: string;
  }>;
  'pagination/download-block': ApiResponseOrError<string>;
  'pagination/download-pagination-issue-manifest': ApiResponseOrError<{
    url: string;
  }>;
  'pagination/auto-paginate': ApiResponseOrError<
    z.infer<typeof OptimizerResponseSchema>
  >;
  'payments/download-dwolla-payouts':
    | { url: string }
    | string
    | { success: false };
  'payments/generate-dwolla-payout':
    | {
        success: boolean;
        csvRows: string[][];
        csvHeaders: string[];
      }
    | { success: false; error: string };
  'payments/:id/refund': ApiResponseOrError<VoidResponse>;
  'payments/cancel-invoice': ApiResponseOrError<VoidResponse>;
  'payments/download-payouts': ApiResponseOrError<{ url: string }>;
  'payments/stripe-login-link': ApiResponseOrError<string>;
  'payments/stripe-bt-payouts-data': ApiResponseOrError<{
    balanceTransactions: EStripePayout[];
  }>;
  'payments/stripe-payout-data': ApiResponseOrError<{
    payouts: EStripePayout[];
  }>;
  'admin/organizations/funeral-homes/convert-to-funeral-home': ApiResponseOrError<{
    success: boolean;
  }>;
  'payments/create-receipt-pdf': ApiResponseOrError<{ receiptUrl: string }>;
  'admin/orders/transfer-ownership': ApiResponseOrError<{ success: boolean }>;
  'admin/display-qa': ApiResponseOrError<DisplayQAResult>;
  'admin/ad-building/download-block': ApiResponseOrError<{ url: string }>;
  'admin/affidavits/notices/:noticeId/get-full-wet-sig-notarization-notices': AffidavitAutomationGetFullWetSigNotarizationNoticesResponse;
  'admin/affidavits/notices/:noticeId/force-assign-affidavit-to-notice': GenericResponse;
  'admin/affidavits/notices/:noticeId/update-affidavit-reconciliation-settings': GenericResponse;
  'admin/affidavits/get-runs-needing-verification': AffidavitAutomationGetRunsToVerifyResponse;
  'admin/affidavits/set-run-unverifiable': GenericResponse;
  'admin/affidavits/wet-signature/create-notarizations': GenericResponse;
  'admin/affidavits/wet-signature/change-notarization-statuses': GenericResponse;
  'admin/affidavits/get-eedition-tracker-data': AdminAffidavitsGetEEditionTrackerDataResponse;
  'admin/automated-affidavit-signers': ApiResponseOrError<AutomatedAffidavitSignersListResponse>;
  'admin/automated-affidavit-signers/:signerId': ApiResponseOrError<AutomatedAffidavitSignerResponse>;
  'admin/checks/:stripeCheckId/refresh': ApiResponseOrError<void>;
  'admin/coupons/:couponId/delete': ApiResponseOrError<void>;
  'admin/coupons/:couponId/update': ApiResponseOrError<void>;
  'admin/coupons/create': ApiResponseOrError<string>;
  'admin/external-upload': AdminExternalUploadResponse;
  'admin/impersonate-user': AdminImpersonateUserResponse;
  'admin/fonts/list': ApiResponseOrError<
    Array<{
      name: string;
      files: Array<{ name: string; format: 'ttf' | 'otf' }>;
    }>
  >;
  'admin/fonts/upload': ApiResponseOrError<AdminFontsUploadResponse>;
  'admin/fonts/:fontName/delete': ApiResponseOrError<AdminFontsDeleteResponse>;
  'admin/inbox-automation/templates': ApiResponseOrError<
    InboxAutomationTemplateResponse[]
  >;
  'admin/inbox-automation/templates/create': ApiResponseOrError<void>;
  'admin/inbox-automation/templates/delete': ApiResponseOrError<void>;
  'admin/inbox-automation/templates/preview': ApiResponseOrError<string>;
  'admin/invoices/detach-invoice-from-notice': GenericResponse;
  'admin/invoices/void-detached-invoice': GenericResponse;
  'admin/invoices/regenerate-invoices': AdminInvoiceRegenerationResponse;
  'admin/invoices/refund-bulk-paid-invoice': ApiResponseOrError<void>;
  'admin/invoices/mark-uncollectible': ApiResponseOrError<MarkUncollectibleOutcome>;
  'admin/subscriptions/invoice-items-report': ApiResponseOrError<AdminSubscriptionsInvoiceItemsReportResponse>;
  'admin/notices/:noticeId/upload-file': GenericResponse;
  'admin/notices/:noticeId/replace-indd': GenericResponse;
  'admin/notices/:noticeId/upload-camera-ready-ad': GenericResponse;
  'admin/notices/:noticeId/create-notice-transfer': ApiResponseOrError<AdminCreateNoticeTransferResponse>;
  'admin/orders/cancel': GenericResponse;
  'admin/orders/void-invoice': ApiResponseOrError<null>;
  'admin/orders/cancel-invoice': ApiResponseOrError<null>;
  'admin/organizations/caches/create': GenericResponse;
  'admin/organizations/caches/modify-description': GenericResponse;
  'admin/organizations/caches/modify-required': GenericResponse;
  'admin/organizations/caches/delete-entry': GenericResponse;
  'admin/organizations/caches/update-entry': GenericResponse;
  'admin/organizations/export-settings': GenericResponse;
  'admin/organizations/invoice-settings': GenericResponse;
  'admin/organizations/plan-settings': GenericResponse;
  'admin/organizations/product-implementation-settings': GenericResponse;
  'admin/organizations/regenerate-affidavits': GenericResponse;
  'admin/organizations/subdomain-settings': AdminOrganizationsSubdomainSettingsResponse;
  'admin/organizations/stripe-settings': GenericResponse;
  'admin/organizations/upload-settings': GenericResponse;
  'admin/organizations/integration-upload-settings': GenericResponse;
  'admin/organizations/build-export-settings': GenericResponse;
  'admin/organizations/deadline-settings': ApiResponseOrError<void>;
  'admin/payments/unreconciled-checks': ApiResponseOrError<EStripePaperCheck[]>;
  'admin/payments/reconciled-checks': ApiResponseOrError<
    SearchableInvoiceRecordWithCheck[]
  >;
  'admin/payments/lookup-stripe-event': ApiResponseOrError<AdminStripeEventSummary>;
  'admin/payments/replay-stripe-event': ApiResponseOrError<void>;
  'pagination/pagination-review': NoticePaginationReviewResponse;
  'pagination/order-pagination-review': OrderPaginationReviewResponse;
  'pagination/pagination-download': PaginationDownloadResponse;
  'pagination/get-pagination-ready-orders': PaginationReadyOrdersResponse;
  'pagination/paginators': PaginatorsResponse;
  'admin/upload-customer-list': GenericResponse;
  'admin/verify-customer-list': GenericResponse;
  'admin/sign-in': AdminSignInResponse;
  'admin/pagination-manifest': AdminPaginationManifestResponse;
  'admin/auto-pagination/update-auto-pagination-settings': ApiResponseOrError<VoidResponse>;
  'admin/auto-pagination/test-auto-pagination': ApiResponseOrError<AutoPaginationResults>;
  'admin/auto-pagination/create-default-settings': ApiResponseOrError<VoidResponse>;
  'admin/sync-to-elastic': AdminSyncToElasticResponse;
  'admin/regenerate-documents': ApiResponseOrError<void>;
  'admin/roles': GenericResponse;
  'admin/reps': GenericResponse;
  'admin/paginators': GenericResponse;
  'notices/async-design/upload-finalized-design': ApiResponseOrError<VoidResponse>;
  'admin/shadow': GenericResponse;
  'admin/unshadow': GenericResponse;
  'admin/unshadow-an-org': GenericResponse;
  'admin/user-management/delete': ApiResponseOrError<void>;
  'admin/user-management/organization-settings': GenericResponse;
  'admin/user-management/search': AdminUserManagementSearchResponse;
  'admin/user-management/update-stripe-id': GenericResponse;
  'admin/user-management/update-user-information': GenericResponse;
  'admin/user-management/update-notification-settings': ApiResponseOrError<void>;
  'admin/user-management/customer-io-messages': ApiResponseOrError<{
    messages: CustomerIOMessage[];
    nextCursor: string | null;
  }>;
  'affidavit-automation/events/create-eedition-uploaded-event': GenericResponse;
  'affidavit-automation/notarization/add-to-notarization-batch': AffidavitAutomationAddToNotarizationResponse;
  'affidavit-automation/notarization/:notarizationID/finalize': AffidavitAutomationFinalizeResponse;
  'affidavit-automation/get-all-notaries-from-proof': AffidavitAutomationGetAllNotariesResponse;
  'affidavit-automation/notarization/get-notices-ready-for-notarization': AffidavitAutomationGetNoticesResponse;
  'affidavit-automation/notarization/get-notices-ready-for-wet-sig-notarization': AffidavitAutomationGetNoticesForWetSigNotarizationResponse;
  'affidavit-automation/notarization/get-notices-assigned-to-mail-centers': AffidavitAutomationGetNoticesAssignedToMailCentersResponse;
  'affidavit-automation/notarization/assign-wet-sig-notices-to-mail-pages': AffidavitAutomationAssignNoticesToMailCentersResponse;
  'affidavit-automation/notarization/process-wet-sig-notarized-affidavit': GenericResponse;
  'affidavit-automation/notarization/override-notarization-preconditions': GenericResponse;
  'affidavit-automation/notarization/:notarizationID/process-complete': ApiResponseOrError<void>;
  'affidavit-automation/send-weekly-digest': GenericResponse;
  'affidavit-automation/manually-verify-runs': GenericResponse;
  'display-ads/unapproved-display-ad-email-alert': GenericResponse;
  'display-ads/ax/:instance/orders/search': DisplayAdsAXOrdersSearchResponse;
  'documents/affidavits/preview': DocumentsAffidavitsPreviewResponse;
  'documents/properties': DocumentsPropertiesResponse;
  'documents/invoice/:invoiceId/download': ApiResponseOrError<string>;
  'documents/invoices/download': ApiResponseOrError<string>;
  'documents/unpaid-invoices-summary/download': ApiResponseOrError<
    string | null
  >;
  'documents/pdf/download': PDFDownloadResponse;
  'documents/generate-proof': ApiResponseOrError<void>;
  'documents/zip': BulkDownloadResponse;
  'documents/create-all-publisher-docs': ApiResponseOrError<void>;
  'documents/cloud-convert': ApiResponseOrError<string>;
  'ftp/parse-filemage-path': GenericResponse;
  'integrations/customers/:newspaperId/find': IntegrationsCustomersFindResponse;
  'integrations/async-design/notice/:noticeId/cancel-build-ad-request': GenericResponse;
  'integrations/async-design/notice/:noticeId/create-build-ad-request': GenericResponse;
  'integrations/visionData/ocr': ApiResponseOrError<IntegrationsVisionDataOcrResponse>;
  'integrations/trigger-event': IntegrationsTriggerEventResponse;
  'invoice-transactions/create-authorization': PaymentGatewayResult;
  'invoice-transactions/create-charge': ApiResponseOrError<ColumnPaymentResponse>;
  'invoice-transactions/create-session': ApiResponseOrError<InvoiceTransactionsCreateSessionResponse>;
  'invoice-transactions/:invoiceId/refund-transactions': InvoiceTransactionsFetchRefundTransactionsResponse;
  'invoices/:invoiceId/pay-invoice-data': ApiResponseOrError<LoadPayInvoiceResponse>;
  'notices/create-invoice': ApiResponseOrError<{ eventId: string }>;
  'notices/:id/cancel': ApiResponseOrError<VoidResponse>;
  'notices/request-cancellation': ApiResponseOrError<VoidResponse>;
  'order-display/newspapers/:newspaperId/:product': ApiResponseOrError<OrderDisplayNewspaperResponse>;
  'orders/apply-coupon': ApiResponseOrError<{ newInvoiceId?: string }>;
  'orders/remove-coupon': ApiResponseOrError<void>;
  'orders/cancel': ApiResponseOrError<void>;
  'orders/refund': ApiResponseOrError<void>;
  'orders/create': OrderCreateResponse;
  'orders/create-invoice': ApiResponseOrError<{ eventId: string }>;
  'orders/download-proofs': ApiResponseOrError<OrdersDownloadProofsData>;
  'orders/finalize-invoice': ApiResponseOrError<void>;
  'orders/grant-access': OrdersGrantAccessResponse;
  'orders/update-price': ApiResponseOrError<void>;
  'orders/:orderId/filer-name': OrderFilerNameResponse;
  'orders/:orderId/submit-free': ApiResponseOrError<void>;
  'orders/:orderId/submit-unpaid': ApiResponseOrError<void>;
  'orders/:orderId/confirm-edits': ApiResponseOrError<void>;
  'orders/:orderId/delete': ApiResponseOrError<void>;
  'payments/create-bank-account': ApiResponseOrError<boolean>;
  'payments/delete-source': ApiResponseOrError<{ deleted: boolean }>;
  'payments/delete-payment-method': ApiResponseOrError<boolean>;
  'payments/download-payout': ApiResponseOrError<{ csvRows: string[][] }>;
  'payments/stripe-cancel-setup-intent': ApiResponseOrError<boolean>;
  'payments/dwolla-payout-data': PaymentsDwollaPayoutDataResponse;
  'payments/list-invoice-payment-methods': PaymentsListInvoicePaymentMethodsResponse;
  'payments/list-customer-bank-accounts': ApiResponseOrError<PaymentsListCustomerBankAccountsResponse>;
  'payments/pay-stripe-invoice': ApiResponseOrError<void>;
  'payments/create-connected-account-link': ApiResponseOrError<{
    url: string;
    accountId: string;
  }>;
  'payments/mark-invoice-paid': ApiResponseOrError<void>;
  'payments/stripe-create-setup-intent': PaymentsStripeCreateSetupIntentResponse;
  'payments/get-bank-account-payment-method-status': PaymentsGetBankAccountPaymentMethodStatusResponse;
  'payments/attach-payment-method-to-customer': GenericResponse;
  'payments/client-secret-for-setup-intent': PaymentsGetClientSecretForSetupIntentResponse;
  'payments/stripe-customer-details': PaymentsStripeCustomerDetailsResponse;
  'publication-issue/orders': GetPublicationIssueOrdersResponse;
  'retool/organizations/:newspaperId/link-newspaper-parent': GenericResponse;
  'search/advertiser-organizations': SearchAdvertiserOrganizationsResponse;
  'search/customers': SearchCustomersResponse;
  'search/invoices': SearchInvoicesResponse;
  'search/orders': SearchOrdersResponse;
  'search/orders/facets': ApiResponseOrError<ElasticOrderFacetsResults>;
  'search/organizations': SearchOrganizationsResponse;
  'search/public-notices': SearchPublicNoticesResponse;
  'search/public-notices/facets': SearchPublicNoticesFacetsResponse;
  'search/usernotices': SearchUserNoticesResponse;
  'site-settings': ApiResponseOrError<ProductSiteSettingCreateResponse>;
  'site-settings/:siteSettingId': ApiResponseOrError<null>;
  'subdomains/:subdomain': ApiResponseOrError<SubdomainQueryResponse>;
  'templates/:adTemplateId/archive': GenericResponse;
  'templates/fork': TemplatesForkResponse;
  'templates/fork-clean': TemplatesForkCleanResponse;
  'payway/queue-transaction': PaywayQueueTransactionResponse;
  'templates/styles': TemplatesStylesResponse;
  'users/invite': ApiResponseOrError<UsersInviteResponse>;
  'rates/generate-code': RatesGenerateCodeResponse;
  'retool/organizations/create-advertiser-org': RetoolCreateAdvertiserOrgResponse;
  'retool/users': UserQueryResponse[];
  'users/create-advertiser': UsersCreateAdvertiserResponse;
  'users/delete-active-member': ApiResponseOrError<void>;
  'users/delete-invited-member': ApiResponseOrError<void>;
  'users/send-password-reset-email': PasswordResetEmailResponse;
  'users/get-auth-providers-by-email': UsersAuthProvidersByEmailResponse;
  'users/get-user-by-email': UsersGetUserByEmailResponse;
  'users/get-users-from-customer-org': UsersGetUsersFromCustomerOrgResponse;
  'users/register-organization': ApiResponseOrError<UsersRegisterOrganizationResponse>;
  'users/register': UsersRegisterResponse;
  'users/change-email': GenericResponse;
  'users/send-change-account-email-link': GenericResponse;
  'users/sync-existing-notices': ApiResponseOrError<VoidResponse>;
  'admin/user-management/migrate-notices-between-users': ApiResponseOrError<VoidResponse>;
  'auth/signin-exchange-token-for-google-microsoft-provider': GoogleMicrosoftSignInResponse;
  'organization-management/merge': GenericResponse;
  'organizations/:subdomain/context': OrgContextState;
  'organizations/related-papers': GetRelatedPapersResponse;
  'organizations/:organizationType/exists': ExistsResponse;
  'organizations/:organizationType/create': ApiResponseOrError<string>;
  'organization-management/delete': GenericResponse;
  'retool/duplicate-newspaper': GenericResponse;
  'customers/get-or-create-with-customer-organization': GetOrCreateCustomerWithCustomerOrganizationResponse;
  'customers/get-or-create-customer': GetOrCreateCustomerWithCustomerOrganizationResponse;
  'ad-pricing/price': AdPriceResponse;
  'image/upscale': ApiResponseOrError<{ url: string }>;
  'ledger/bulk-pay-invoices': ApiResponseOrError<string>;
  'ledger/receipt': ApiResponseOrError<string>;
  'search/pagination-table-rows': ApiResponseOrError<
    ReportsPaginationReportRow[]
  >;
  'search/adjudication-areas': ApiResponseOrError<SearchAdjudicationAreaResponse>;
  'search/publishers/by-location': ApiResponseOrError<{
    county: string;
    state: string;
    publishers: {
      id: string;
      name: string;
      organizationStatus: number;
      noticeVolume: number;
    }[];
    count: number;
  }>;
  'search/invoices-by-aging-aggregations': ApiResponseOrError<
    AccountingTableRowData[]
  >;
  'search/notices-and-orders-by-month-aggregations': ApiResponseOrError<ProductRevenueByMonthChartData>;
  'search/customer-notices-and-orders-cohorts': ApiResponseOrError<
    CustomersTableRow[] | null
  >;
  'search/product-category-aggregations': ApiResponseOrError<
    ProcessedProductsCategoryData[]
  >;
  'search/unpaid-invoices': ApiResponseOrError<SearchableInvoiceRecord[]>;
  'experiments/chat-complete': ApiResponseOrError<
    z.infer<typeof ExperimentDraftCompletionResponseSchema>
  >;
  'stripe/customer/create': ApiResponseOrError<StripeCustomerCreateResponse>;
  'bulk-export': void;
  'admin/integrations/resync': ApiResponseOrError<void>;
  'admin/integrations/settings': ApiResponseOrError<void>;
  'admin/api-clients/create': ApiResponseOrError<{
    client: ApiClient;
    rawKey: string;
  }>;
  'admin/api-clients/update': ApiResponseOrError<{ success: boolean }>;
  'admin/api-clients/list': ApiResponseOrError<({ id: string } & ApiClient)[]>;
  'admin/api-clients/revoke': ApiResponseOrError<{ success: boolean }>;
};

/** Notice-level ARS made API-friendly */
type AdminNoticesUpdateAffidavitSettingsRequest = Partial<{
  affidavitsManagedByColumn: boolean;
  notarizationRequired: boolean;
  managedAffidavitTemplateId: string;
  managedAffidavitTemplateStoragePath: string;
  notarizationVendor: 'notarize' | 'manual';
  requiresInStateNotary: boolean;
  assignedMailCenter: number;
  overrideNotarizationPreconditions: boolean;
}>;

type AffidavitAutomationNotarizationAddToNotarizationRequest = {
  noticeIDs: string[];
  assignments?: {
    signer: AutomatedAffidavitSignerValue;
    notary: AutomatedAffidavitNotaryValue;
  };
};

type AffidavitAutomationFinalizeRequest = Record<string, never>;

type AffidavitAutomationOverrideNotarizationPreconditionsRequest = {
  noticeIds: string[];
};

type AffidavitAutomationBulkNotarizationRequest = {
  includeUnverifiedAffidavits: boolean;
  affidavitSigningFirstName: string;
  affidavitSigningLastName: string;
  affidavitSigningPhone: string;
  affidavitSigningEmail: string;
};

type AffidavitAutomationSendWeeklyDigestRequest = {
  newspaperId: string;
};

type AffidavitAutomationCreateEEditionUploadedEventRequest = {
  newspaperId: string;

  storagePath: string;

  uploadMethod: UploadMethod;

  /**
   * format: MM/DD/YYYY
   */
  runDateString: string;
};

type AffidavitAutomationAssignWetSigNoticesToMailPagesRequest = {
  assignedBatches: AutomatedAffidavitNotarizationBatch[];
};

type AffidavitAutomationForceAssignAffidavitToNoticeRequest = {
  affidavitStoragePath: string;
};

type AffidavitAutomationGetNoticesForWetSigNotarizationRequest = {
  includeAlreadyAssignedNotices: boolean;
};

type AffidavitAutomationGetNoticesWithMailCenterAssignmentsRequest = {
  mailCenter: number | 'all mail center assignments';
  includeAffidavitUrlAndAddresses?: boolean;
  activeOrPast?: 'active' | 'past';
};

type AffidavitAutomationProcessWetSigNotarizedAffidavitRequest = {
  noticeId: string;

  /**
   * File uploads from Retool convert the data to a string. We may
   * wish to update this request type if we move mail center pages to
   * Column Admin if we wish to pass file data via download URL or
   * storage path instead of stringified raw data
   */
  fileData: string;
  fileNameWithExtension: string;
};

type AffidavitAutomationNotarizationProcessCompleteRequest = {
  notarizationID: string;
};

type AffidavitAutomationGetRunsToVerifyRequest = {
  parentIds: string[];
};

type AffidavitAutomationSetUnverifiableRequest = {
  noticeId: string;
  /**
   * YYYY-MM-DD formatted date string
   */
  runDate: string;
  reason?: string;
};

type AffidavitAutomationManuallyVerifyRunsRequest = {
  runIds: string[];
};

export const PaymentsCancelInvoiceRequestSchema = z.object({
  noticeId: z.string().nonempty(),
  voidReason: z.string().optional()
});
type PaymentsCancelInvoiceRequest = z.infer<
  typeof PaymentsCancelInvoiceRequestSchema
>;

type PaymentsCreateBankAccountRequest = {
  stripeId: string;
  token: string;
};

type PaymentsDeleteSourceRequest = {
  stripeId: string;

  /**
   * The Stripe source ID (not the source document ID in our database)
   */
  sourceId: string;
};

type PaymentsDeletePaymentMethodRequest = {
  /**
   * The ID of the payment method that is being detached
   */
  bankAccount: string;
};

type PaymentsDownloadPayoutRequest = {
  payout: EStripePayout | EPayoutData;
  accountId: string;
};

type PaymentsStripeCancelSetupIntentsRequest = {
  /**
   * The ID of the payment method that is being detached
   */
  bankAccount: string;
};

type PaymentsListInvoicePaymentMethodsRequest = {
  invoiceId: string;
};

export const PaymentsListCustomerBankAccountsRequestSchema =
  z.discriminatedUnion('type', [
    z.object({ type: z.literal('individual') }),
    z.object({
      type: z.literal('organization'),
      organizationId: z.string().nonempty()
    })
  ]);
type PaymentsListCustomerBankAccountsRequest = z.infer<
  typeof PaymentsListCustomerBankAccountsRequestSchema
>;

export const PaymentsListCustomerBankAccountsResponseSchema = z.object({
  bank_accounts_sources: z.array(StripeBankAccountSchema),
  bank_accounts_payment_methods: z.array(StripePaymentMethodUSBankAccountSchema)
});
type PaymentsListCustomerBankAccountsResponse = z.infer<
  typeof PaymentsListCustomerBankAccountsResponseSchema
>;

type PaymentsPayInvoiceRequest = {
  amount: number;
  customerEmail: string;
  /**
   * For orders, this invoice id is tied to a specific version and any version-specific
   * operations will pull the version data from the invoice orderVersion
   */
  invoiceId: string;

  /**
   * The id of a Stripe source representing a US bank account.
   */
  stripeBankAccountSourceId?: string;

  userId?: string;
  /**
   * This is the Stripe ID for a payment method reference (src_kf73jdf…, etc.)
   */
  paymentMethodId?: string;

  /**
   * This is the human-readable payment method for in-app processed payments
   */
  paymentMethod: PaymentMethods;

  /**
   * If the user opts in, attach the card information to their Stripe customer.
   */
  saveCreditCard?: boolean;

  /**
   * Used to control whether we detach the payment method in old invoice code
   * Can be removed once pay invoice refactor is fully live
   */
  isSavedPaymentMethod?: boolean;

  /**
   * Used to kick off additional logging for a specific orders dashboard
   */
  isOrder?: boolean;

  /**
   * Used to kick off additional logging for a specific orders by product dashboard
   */
  product: Product;
};

type PaymentsMarkInvoicePaidRequest = {
  /**
   * For orders, this invoice id is tied to a specific version and any version-specific
   * operations will pull the version data from the invoice orderVersion
   */
  invoiceId: string;
  payBySupport?: boolean;
  manualPaymentDetails?: {
    note: string;
    userId: string;
  };
  customer_email: string;
  paymentMethod?: PaymentMethods;
};

type PaymentsStripeCreateSetupIntentRequest = {
  /**
   * The ID of the customer creating a setup intent
   */
  customer: string;
};

type PaymentsGetBankAccountPaymentMethodStatusRequest = {
  /**
   * The ID of the payment method whose status we are retrieving
   */
  payment_method: string;
};

type PaymentsAttachPaymentMethodToCustomerRequest = {
  /**
   * The ID of the payment method being attached to customer
   */
  payment_method: string;

  /**
   * The ID of the customer who we are attaching the payment method to
   */
  customer: string;
};

type PaymentsGetClientSecretForSetupIntentRequest = {
  /**
   * The ID of the payment method whose setup intent's client secret we need to obtain
   */
  payment_method: string;
};

type PaymentsStripeCustomerDetailsRequest = {
  /**
   * The ID of the customer whose Stripe info we are trying to retrieve
   */
  customer: string;
};

type UsersCreateAdvertiserRequest = {
  parent: string;
  organizationType: OrganizationTypeValue;
  orgName: string;
  address: string;
  city: string;
  state: number;
  zipCode: string;
  phone: string;
  subOrg?: ERef<EOrganization>;
  website?: string;
  createdBy?: string;
  addressLine2?: string;
};

type UsersDeleteActiveMemberRequest = {
  userId: string;
  organizationId: string;
};

type SearchUserNoticesRequest = {
  search: string;
  sort?: Record<string, SORT_DIRECTIONS>[];
  current?: number;
  size?: number;
  isPublisher: boolean;
  showAllOrgsNotices?: boolean;

  /**
   * User's current active organization. This is taken from the client
   * rather than the server because it may have changed recently.
   */
  activeOrganizationId: string | undefined;

  /**
   * Used for Column Reports -- return notices for specified organization ids
   */
  specifiedOrganizationIds?: string[];

  /**
   * When the user selects "My Notices" in the dropdown.
   */
  showUserOnlyNotices?: boolean;

  isArchivedTab?: boolean;
  isDraftTab?: boolean;

  /**
   * When true, only the explicit SearchableNoticeRecordFilter fields below
   * are considered.
   */
  onlyExplicitFilters?: boolean;

  /**
   * Filters which are AND-ed together.
   */
  filters?: SearchableNoticeRecordFilter[];

  /**
   * Filters which are OR-ed together;
   */
  anyFilters?: SearchableNoticeRecordFilter[];

  /**
   * Filters which must not match
   */
  noneFilters?: SearchableNoticeRecordFilter[];
};

type SearchUserNoticesFacetRequest = {
  search: string;
  facets: SearchableNoticeRecordFacet;
  filters?: SearchableNoticeRecordFilter[];
  showAllOrgsNotices?: boolean;
  hasinvoice?: boolean;
};

type SearchOrganizationsRequest = {
  search: string;
  filters: SearchableOrganizationRecordFilter[];
  noneFilters?: SearchableOrganizationRecordFilter[];
  pageSize?: number;
  fetchAllOrganizations?: boolean;
};

type SearchOrdersRequest = {
  search: string;
  filters: SearchableNewspaperOrderRecordFilter[];
  sort: Record<string, SORT_DIRECTIONS>[];
  pageSize?: number;
  pageNumber?: number;
};

type SearchOrdersFacetsRequest = {
  search: string;
  filters: SearchableNewspaperOrderRecordFilter[];
  facets: SearchableNewspaperOrderRecordFacet;
};

type SearchInvoicesRequest = {
  search: string;
  allFilters: SearchableInvoiceRecordFilter[];
  anyFilters?: SearchableInvoiceRecordFilter[];
  sort: Record<string, SORT_DIRECTIONS>[];
  pageSize?: number;
};

type SearchPublicNoticesRequest = {
  search: string;
  allFilters: SearchablePublicNoticeRecordFilter[];
  sort: Record<string, SORT_DIRECTIONS>[];
  noneFilters?: SearchablePublicNoticeRecordFilter[];
  pageSize?: number;
  current?: number;
  isDemo?: boolean;
};

type SearchPublicNoticesFacetsRequest = {
  search: string;
  facets: SearchablePublicNoticeRecordFacet;
  allFilters: SearchablePublicNoticeRecordFilter[];
  noneFilters: SearchablePublicNoticeRecordFilter[];
  isDemo?: boolean;
};

type SearchCustomersRequest = {
  search: string;
  filters: SearchableCustomerRecordFilter[];
  pageSize?: number;
};

type SearchAdjudicationAreaRequest = {
  search: string;
  filters: SearchableAdjudicationAreaRecordFilter[];
  pageSize?: number;
};

type ColumnReportsBaseRequest = {
  orgIds: string[];
  startTimestamp: number;
  endTimestamp: number;
};

interface CreatePublicNoticePaidSubscriptionRequest
  extends Pick<
    ESubscription,
    'email' | 'frequency' | 'elasticFilters' | 'isPaidSubscription' | 'query'
  > {
  paymentId: string;
  subInterval: string;
  subIntervalCount: number;
  product: string;
  productPrice: number;
  organizationId?: string | null;
}

interface CreatePublicNoticeFreeSubscriptionRequest
  extends Pick<
    ESubscription,
    | 'email'
    | 'frequency'
    | 'elasticFilters'
    | 'query'
    | 'isPaidSubscription'
    | 'phone'
  > {
  organizationId?: string | null;
}

interface UpdateSubscriptionQueryRequest
  extends Pick<ESubscription, 'elasticFilters' | 'query'> {
  subId: string;
  subscriptionType: 'flatRate' | 'free' | 'optionalRate';
  frequency: string;
}

type TemplatesForkRequest = {
  downloadUrl: string;
  styles: Omit<ETemplateStyles, 'id'>;
};

type TemplatesStylesRequest = {
  templateId: string;
  forceRefresh?: boolean;
};

type AffidavitAutomationStakInitiationRequest = {
  newspaperId: string;
  eEditionURL: string;
  date: string; // format MM/DD/YYYY
};

type PaywayQueueTransactionRequest = {
  paywayRequestToken: string;
  creditCardNumber: string;
  expirationDate: string;
  firstName: string;
  lastName: string;
  fullAddress: string;
  city: string;
  stateAbbrev: string;
  zip: string;
  email: string;
  phone: string;
  noticeId: string;
  paymentName: string;
};

type PDFDownloadRequest = {
  noticeId: string;
  rasterize: boolean;
};

type AddUploadIdRequest = {
  uploadID: string;
  newspaperName: string;
  counties: [string, ...string[]];
  subdomain: DisplaySiteSubdomain;
  organizationId: string | null;
};

type SetPreferCustomerOnParentRequest = {
  parentID: string;
  newSetting: boolean | undefined;
};

type IntegrationsCustomersFindRequest = {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: number | '' | null;
  zip?: string;
  organizationName?: string;
};

type IntegrationsCancelBuildAdRequest = {
  buildAdRequestEventId: string;
};

type IntegrationsCreateBuildAdRequest = {
  requestingUserId: string;
  orderNumber: AffinityXOrderNumber | null;
  numberOfColumns: number;
  approxHeightInInches: number;
  pageCount: number;
};

type PaymentsGenerateDwollaPayoutRequest = {
  payout: PayoutParentRowData;
};

type UsersSendPasswordResetEmailRequest = {
  email: string;
};

export const UsersMigrateNoticesBetweenAccountsRequestSchema = z.object({
  oldUserId: z.string().nonempty(),
  newUserId: z.string().nonempty(),
  newOrganizationId: z.string().optional(),
  migrateDraftNotices: z.boolean().optional()
});
type UsersMigrateNoticesBetweenAccountsRequest = z.infer<
  typeof UsersMigrateNoticesBetweenAccountsRequestSchema
>;

export const AdminNoticesChangeFilerRequestSchema = z.object({
  newUserId: z.string().nonempty(),
  newOrganizationId: z.string().optional()
});
type AdminNoticesChangeFilerRequest = z.infer<
  typeof AdminNoticesChangeFilerRequestSchema
>;

export const AdminNoticesCancelInvoiceRequestSchema = z.object({
  voidReason: z.string().optional()
});
type AdminNoticesCancelInvoiceRequest = z.infer<
  typeof AdminNoticesCancelInvoiceRequestSchema
>;

/**
 * A single storage path segment under `fonts/`; `/list` ignores anything nested deeper.
 * Validates without trimming: delete matches the exact names `/list` returns.
 */
export const FontPathSegmentSchema = z
  .string()
  .refine(segment => segment.trim() !== '', 'Must not be blank')
  .refine(segment => !segment.includes('/'), 'Must not contain "/"');

export const AdminFontsUploadRequestSchema = z.object({
  fontName: FontPathSegmentSchema,
  files: z
    .array(
      z.object({
        name: FontPathSegmentSchema,
        data: z.string().nonempty()
      })
    )
    .min(1)
});
type AdminFontsUploadRequest = z.infer<typeof AdminFontsUploadRequestSchema>;

export const AdminFontsDeleteRequestSchema = z.object({
  filesToDelete: z.array(FontPathSegmentSchema).min(1).optional()
});
type AdminFontsDeleteRequest = z.infer<typeof AdminFontsDeleteRequestSchema>;

const FontFileSchema = z.object({
  name: z.string(),
  format: z.enum(['ttf', 'otf'])
});

export const AdminFontsUploadResponseSchema = z.object({
  fontName: z.string(),
  uploadedFiles: z.array(FontFileSchema),
  isNewFont: z.boolean(),
  message: z.string()
});
type AdminFontsUploadResponse = z.infer<typeof AdminFontsUploadResponseSchema>;

export const AdminFontsDeleteResponseSchema = z.object({
  message: z.string(),
  fontName: z.string()
});
type AdminFontsDeleteResponse = z.infer<typeof AdminFontsDeleteResponseSchema>;

type UsersRegisterRequest = {
  firstName: string;
  lastName: string;
  password: string;
  email: string;
  inviteId?: string;
  organizationId?: string;
  organizationName?: string;
  role?: number;
  hasTemporaryPassword?: boolean;
};

type UsersSendChangeAccountEmailLinkRequest = {
  email: string;
};

type UsersChangeEmailRequest = {
  token: string;
};

type SearchAdvertiserOrganizationsRequest = {
  organizationType: number;
};

type RetoolManualSyncRequest = {
  noticeId: string;
  userId: string;
};

type RetoolLinkNewspaperParentRequest = {
  parentId: string;
};

type RetoolNewAdvertiserOrgRequest = {
  name: string;
  organizationType:
    | typeof OrganizationType.government.value
    | typeof OrganizationType.law_firm.value
    | typeof OrganizationType.other_organization.value;
  address: string;
  addressLine2?: string;
  city: string;
  state: number;
  zipCode: string;
  phone: string;
  website?: string;
  email: string;
  firstName: string;
  lastName: string;
};

type InvoicesCreateBulkInvoicesLoopRequest = {
  advertisers: boolean;
  advertiserOrganizations: boolean;
};

type InvoicesCreateBulkInvoicesRequest = {
  advertiserOrgId: string | undefined;
  advertiserId: string | undefined;
  startAt: string | undefined;
  endAt: string | undefined;
};

type GenerateProofRequest = {
  draftId: string;
};

export const IntegrationsVisionDataOcrRequestSchema = z.object({
  screenshotUrl: z.string().nonempty(),
  target: z.union([
    z.object({
      type: z.literal('text'),
      search: z.string().nonempty()
    }),
    z.object({
      type: z.literal('ad-number')
    }),
    z.object({
      type: z.literal('locked-ad')
    })
  ])
});

type IntegrationsTriggerEventRequest = {
  eventType: SyncTriggerEvent['type'];
  noticeId: string;
  invoiceId?: string;
  ignoreTriggers?: boolean;
};

type OrganizationManagementMergeRequest = {
  correctOrganizationId: string;
  duplicateOrganizationId: string;
  recordPreservationSettings?: RecordPreservationSettings;
  ignoreCustomSettings?: IgnoreCustomSettings;
};

// TODO: It is not safe to pass ERate via the API
export type DuplicateNewspaperInput = Partial<
  Omit<EOrganization, 'defaultLinerRate' | 'defaultDisplayRate'> & {
    defaultLinerRate: ERate;
    defaultDisplayRate: ERate;
  }
>;

type RetoolDuplicateNewspaperRequest = {
  organizationId: string;
  newOrganizationName?: string;
  newOrganizationData?: DuplicateNewspaperInput;
};

type UsersGetUserByEmailRequest = {
  email: string;
};

type UsersGetUsersFromCustomerOrgRequest = {
  customerOrganizationId: string;
};

export const UsersRegisterOrganizationRequestSchema = z.object({
  organizationType: z.union([
    z.literal(OrganizationType.law_firm.value),
    z.literal(OrganizationType.government.value),
    z.literal(OrganizationType.other_organization.value),
    z.literal(OrganizationType.funeral_home.value)
  ]),
  name: z.string().nonempty().max(MAX_ORGANIZATION_NAME_LENGTH),
  addressLine1: z.string().nonempty(),
  addressLine2: z.string().optional(),
  city: z.string().nonempty(),
  state: z.number().int().positive(),
  zipCode: z.string().nonempty(),
  phone: z.string().nonempty(),
  email: z.string().nonempty()
});
type UsersRegisterOrganizationRequest = z.infer<
  typeof UsersRegisterOrganizationRequestSchema
>;

export const UsersRegisterOrganizationResponseSchema = z.object({
  organizationId: z.string()
});
type UsersRegisterOrganizationResponse = z.infer<
  typeof UsersRegisterOrganizationResponseSchema
>;

export const UsersInviteRequestSchema = z.object({
  inviteData: z
    .array(
      z.object({
        email: z.string().nonempty(),
        role: z.number().int(),
        resend: z.boolean().optional()
      })
    )
    .min(1)
    .max(5),
  organizationId: z.string().nonempty(),
  isExistingUserInvite: z.boolean()
});
type UsersInviteRequest = z.infer<typeof UsersInviteRequestSchema>;

export const UsersInviteResponseSchema = z.object({
  invited: z.array(z.string()),
  alreadyHaveInvitesToCurrentOrg: z.array(z.string()),
  resend: z.boolean().optional(),
  emailsLinkedToNotices: z.array(z.string()),
  alreadyLinkedToIncompatibleOrganization: z.array(z.string()),
  alreadyInvitedToIncompatibleOrganization: z.array(z.string()),
  alreadyMembersOfInvitedOrg: z.array(z.string()),
  invitesUnsnoozed: z.number()
});
export type UsersInviteResponse = z.infer<typeof UsersInviteResponseSchema>;

export const UsersUpdateRoleRequestSchema = z.object({
  userId: z.string().nonempty(),
  organizationId: z.string().nonempty(),
  role: z.number().int()
});
type UsersUpdateRoleRequest = z.infer<typeof UsersUpdateRoleRequestSchema>;

export const UsersAcceptJoinRequestRequestSchema = z.object({
  joinRequestId: z.string().nonempty(),
  role: z.number().int()
});
type UsersAcceptJoinRequestRequest = z.infer<
  typeof UsersAcceptJoinRequestRequestSchema
>;

export const UsersDeclineJoinRequestRequestSchema = z.object({
  joinRequestId: z.string().nonempty()
});
type UsersDeclineJoinRequestRequest = z.infer<
  typeof UsersDeclineJoinRequestRequestSchema
>;

export const UsersSnoozeJoinRequestRequestSchema = z.object({
  joinRequestId: z.string().nonempty()
});
type UsersSnoozeJoinRequestRequest = z.infer<
  typeof UsersSnoozeJoinRequestRequestSchema
>;

export const UsersDeleteInvitedMemberRequestSchema = z.object({
  inviteId: z.string().nonempty()
});
type UsersDeleteInvitedMemberRequest = z.infer<
  typeof UsersDeleteInvitedMemberRequestSchema
>;

type OrganizationManagementDeleteRequest = {
  organizationId: string;
  recordPreservationSettings?: RecordPreservationSettings;
};

type GetRelatedPapersRequest = {
  subdomain: string;
  product: Product;
};

type AdminUserManagementDeleteRequest = {
  userId: string;
  skipDeleteAuth: boolean;
};

type FTPParseFilemagePathRequest = {
  path: string;
};

type InvoicesDownloadRequest = {
  invoiceIds: string[];
};

type UnpaidInvoicesSummaryDownloadRequest = {
  advertiserOrganizationId: string;
};

type AdminSignInRequest = {
  token: string;
};

type GoogleMicrosoftSignInRequest = {
  token: string;
  userProfile: Record<string, string>;
  uid: string;
  type: SigninProviderType;
};

type AdminShadowRequest = {
  userId?: string;
  userEmail?: string;
  orgId?: string;
};

type AdminUnshadowOrgRequest = {
  orgId: string;
};

type DocumentsPropertiesRequest = {
  url?: string;
  filePath?: string;
  fileType: FileType;
  fileFormat: string;
};

export const UploadFinalizedDesignRequestSchema = z.object({
  noticeId: z.string(),
  url: z.string().url(),
  fileType: z.enum([FileType.PDF, FileType.JPG]),
  columns: z.number().min(1).max(6).int().optional(),
  modularSizeId: z.string().optional()
});
type UploadFinalizedDesignRequest = z.infer<
  typeof UploadFinalizedDesignRequestSchema
>;

type AdminRegenerateDocumentsRequest = {
  noticeId: string;
  docType: RegenDocType;
};

type AdminSyncToElasticRequest = {
  documentType: ElasticDocumentType;
  documentId: string;
};

type AdminUploadCustomerListRequest = {
  publisherID: string;
  csvContents: string;
};

type PaginationReviewNoticeRequest = {
  publicationIssueId: string;
  manifestStoragePath: string;
  attachmentStoragePaths: string[];
};

export type PaginationReviewOrderRequest = {
  publicationIssueId: string;
  sectionId: string;
  manifestStoragePath: string;
  attachmentStoragePaths: string[];
};

type PaginationDownloadRequest = {
  publicationIssueId: string;
  product: Product;
  publishingMedium: PublishingMedium;
};

type PaginationGetOrdersRequest = {
  publicationIssueId: string;
  product: Product;
  publishingMedium: PublishingMedium;
};

type AdminPaginationManifestRequest = {
  manifestStoragePath: string;
  attachmentStoragePaths: string[];
};

type AdminRepsRequest = {
  userEmail: string;
  isColumnRep: boolean;
};

type AdminPaginatorsRequest = {
  userEmail: string;
  isPaginator: boolean;
  userColor?: string;
};

type AdminRoleRequest = {
  /**
   * Optional parameter to limit role change to a specific organization. If excluded, applies
   * `role` to all organizations.
   */
  orgId?: string;
  /**
   * The role to apply to the user. Applies only to the specified organization if `orgId` is
   * included in the request.
   */
  role: number;
  updateRoleWithChildOrg?: boolean;
};

type DraftsDeleteRequest = {
  noticeId?: string;
  isdraft: boolean;
  draftownerid?: string;
  ispublisherdraft: boolean;
};

type AdminExternalUploadRequest = {
  newspaperId: string;
  startDate: string;
  endDate: string;
};

type AdminUserManagementSearchRequest = {
  searchTerm: string;
};

type AdminUserManagementStripeSettingsRequest = {
  userId: string;
  newStripeId: string;
  oldStripeId: string;
  updatedBy: string;
};

type AdminUserManagementUserInformationRequest = {
  userId: string;
} & Pick<
  EUser,
  | 'address'
  | 'addressLine2'
  | 'city'
  | 'state'
  | 'zipCode'
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'occupation'
>;

type AdminUserManagementUserNotificationsRequest = {
  userId: string;
} & Pick<EUser, 'receiveOrganizationNotifications' | 'notifications'>;

export type CustomerIOMessage = {
  id: string;
  created: number;
  subject: string;
  type: string;
  metrics: {
    delivered?: number;
    processed?: number;
    sent?: number;
  };
  recipient: string;
};

type AdminUserManagementCustomerIOMessagesRequest = {
  email: string;
  cursor?: string;
};

type AdminInvoiceDetachRequest = {
  noticeId: string;
};

type AdminInvoiceVoidDetachedRequest = {
  detachedInvoiceId: string;
};

type AdminOrganizationsStripeSettingsRequest = {
  payColumnInvoices?: PayColumnInvoices;
  updatedBy: string;
  oldStripeId: string;
  newStripeId: string;
  orgId: string;
  applicationFeeSettings: EOrganization['applicationFeeSettings'];
  transferTrigger: EOrganization['transferTrigger'];
};

type AdminOrganizationsSubdomainSettingsRequest = {
  orgId: string;
  subdomain: string;
};

type AdminInvoiceRegenerationRequest = {
  userId?: string;
  orgId?: string;
  stripeId: string;
};

export type AdminSubscriptionsInvoiceItemsReportRequest = {
  stripeInvoiceId: string;
  stripeSubscriptionId: string;
};

export type OrganizationMembership = {
  organizationId: string;
  role: number;
};

type AdminUserManagementOrganizationSettingsRequest = {
  userId: string;
  orgs: OrganizationMembership[];
};

type AdminOrganizationsRegenerateAffidavitsRequest = {
  organizationId: string;
};

type AdminImpersonateUserRequest = {
  userEmail: string;
};

type AdminOrganizationsIntegrationExportSettings = AdminDocumentUpdateRequest<
  EOrganization,
  'xmlExport'
>;

type AdminOrganizationsIntegrationUploadSettings = AdminDocumentUpdateRequest<
  EOrganization,
  'integrationUploadSettings'
>;

type AdminOrganizationsBuildExportSettings = AdminDocumentUpdateRequest<
  EOrganization,
  'buildExport'
>;

type AdminOrganizationsInvoiceSettings = AdminDocumentUpdateRequest<
  EOrganization,
  'editRunDatesAfterInvoicePaid'
>;

type AdminOrganizationsUploadSettings = AdminDocumentUpdateRequest<
  EOrganization,
  'productUploadSettings'
>;

type AdminOrganizationsPlanSettingsRequest = AdminDocumentUpdateRequest<
  EOrganization,
  'planSettings'
>;

type AdminOrganizationsProductSettingRequest = {
  documentId: string;
  classifiedStatus: ImplementationStatus;
  obituaryStatus: ImplementationStatus;
  noticeStatus: number;
};

export const AdminPaymentsUnreconciledChecksRequestSchema = z.object({
  reconciled: z.boolean().optional()
});

export const AdminStripeEventRequestSchema = z.object({
  /**
   * The Stripe event id to look up or replay (evt_*).
   */
  stripeEventId: z.string().trim().min(1)
});

/**
 * A summary of a Stripe event stored in our `stripeevents` firestore collection,
 * surfaced in Column Admin so we can inspect and replay it.
 */
export const AdminStripeEventSummarySchema = z.object({
  id: z.string(),
  type: z.string(),
  created: z.number(),
  livemode: z.boolean(),
  apiVersion: z.string().nullable(),
  successfulColumnProcessing: z.boolean(),
  /**
   * The id of the object the event is about (e.g. the invoice or charge id), if any.
   */
  objectId: z.string().nullable(),
  /**
   * The full raw Stripe event, for display in the admin UI.
   */
  event: z.unknown()
});
export type AdminStripeEventSummary = z.infer<
  typeof AdminStripeEventSummarySchema
>;

type OrdersGrantAccessRequest = {
  authEmail: string;
  accessCode: string;
  editVersion?: number;
  forFuneralHomeVerification: boolean;
};

type OrdersConfirmEditsRequest = {
  version: number;
  refundRequestedInCents: number;
};

type OrdersGetFontResourcesRequest = {
  styles: Required<ETemplateStyles>;
};

type OrdersDownloadProofsRequest = {
  orderId: string;
  version: number;
};

type AdminOrganizationIntegrationCreateCache = {
  newspaperId: string;
  cacheId: string;
  data: ECache;
};

type AdminOrganizationIntegrationModifyCacheDescription = {
  newspaperId: string;
  cacheId: string;
  description: string;
};

type AdminOrganizationIntegrationModifyRequiredDescription = {
  newspaperId: string;
  cacheId: string;
  required: boolean;
};

type AdminOrganizationIntegrationCacheEntry = {
  newspaperId: string;
  cacheId: string;
  newKey: string;
  newValue: string;
  /** Optional property to distinguish create from update. */
  currentKey?: string;
  description?: string;
};

type AdminOrganizationIntegrationDeleteCacheEntry = {
  newspaperId: string;
  cacheId: string;
  deleteKey: string;
};

export const RefundInvoiceRequestSchema = z.object({
  refundReason: z.string(),
  refundAmount: z.string(),
  isInvoiceCancellation: z.boolean()
});
type RefundInvoiceRequest = z.infer<typeof RefundInvoiceRequestSchema>;

type OrdersCreateInvoiceRequest = {
  orderId: string;
  version: number;
  /**
   * Forces creation of a new invoice even if guard code suggests it is not needed
   * e.g. use case: applying a coupon on an edit that doesn't have a price change
   */
  force?: boolean;
};

type AdPriceRequest = {
  adId: string;
  adType: Product;
};

type DocumentsAffidavitsPreviewRequest = {
  affidavitData: AffidavitData;
  templateHandlebars: string;
  logoSource: AffidavitTemplateV2['logoSource'];
  newspaperId: string;

  logoHeightInInches?: number;
  blockWidthInInches?: number;
  fontSize?: number;
};

type AdminNoticesUploadFileRequest = {
  filePath: string;
  downloadUrl: string;
};

type AdminReplaceInddRequest = {
  storagePath: string;
};

type AdminNoticesUploadCameraReadyAdRequest = {
  filePath: string;
  columns: number;
};

type AdminOrderCancelRequest = {
  orderId: string;
  cancellationReason: string;
};

type OrderCancelRequest = {
  orderId: string;
  cancellationReason: string;
  anonymousUserAuthInfo: { authEmail: string; accessCode: string } | null;
};

type GetPublicationIssueOrdersRequest = {
  publicationIssueId: string;
  product: Product;
  publishingMedium?: PublishingMedium;
};

type DisplayAdsUnapprovedDisplayAdEmailAlertRequest = {
  emailData: DisplayAdEmailNotification[];
};

type DisplayAdsAXOrdersSearchRequest = {
  fromDate: string;
  toDate: string;
};
type DisplayAdsAXOrdersSearchResponse = AXOrder[];

type AdminAffidavitsWetSignatureCreateNotarizationsRequest = {
  batches: {
    state: number;
    items: Pick<SerializedAssignNoticesItem, 'mailing' | 'notice'>[];
  }[];
};

type AdminAffidavitsWetSignatureChangeNotarizationStatusesRequest = {
  notarizationIds: string[];
  status: NotarizationStatus;
};

export type AutomatedAffidavitSignerResponse = {
  id: string;
  data: AutomatedAffidavitSigner;
};

export type AutomatedAffidavitSignersListResponse =
  AutomatedAffidavitSignerResponse[];

export type AutomatedAffidavitSignerRequestBody = Omit<
  AutomatedAffidavitSigner,
  'value' | 'createdAt' | 'updatedAt'
>;

type GetOrCreateCustomerWithCustomerOrganizationRequest = {
  userId: string;
  organizationId: string;
  publisherId: string;
};

type GetOrCreateCustomerRequest = {
  userId: string;
  publisherId: string;
};

type AdminDisplayQARequest = {
  affinityXFile: string;
  referenceFile: string;
  originalFile: string;
};

export type DownloadablePaginationElement = {
  fileId?: string | null;
  id: string;
  width: number;
  height: number;
  x: number;
  y: number;
  columnWidth: number;
  bordered?: {
    thickness: number;
  };
};

type PaymentsCreateConnectedAccountLinkRequest = {
  organizationId: string;
  returnUrl: string;
  refreshUrl: string;
};
type AdminDownloadBlockRequest = {
  newspaperId: string;
  elements: DownloadablePaginationElement[];
  fileName: string;
};

type PaginationDownloadManifestRequest = {
  product: string;
  medium: string;
  publicationIssueId: string;
  publicationIssueSectionId?: string;
  filingType: string;
};

export const NoticesCancelRequestSchema = z.object({
  cancellationReason: z.string().nonempty(),
  isRefund: z.boolean()
});

export const NoticesRequestCancellationRequestSchema = z.object({
  id: z.string().nonempty(),
  cancellationReason: z.string().nonempty()
});

export type ElasticOrdersAggregationParams = {
  publisherId: string;
  publicationDate: number;
  medium: string;
};

export const LedgerBulkPayInvoicesRequestSchema = z.object({
  invoiceIds: z.array(z.string()).min(2),
  userId: z.string().min(20),
  organizationId: z.string().min(20).optional(),
  stripePaymentMethodId: z.string().startsWith('pm_')
});

export const AdminAffidavitsGetEEditionTrackerDataRequestSchema = z.object({
  parentIds: z.array(z.string()).nullable(),
  publicationDateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  publicationDateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  expectedUploadMethod: z.nativeEnum(EEditionUploadMethod).nullable(),
  eeditionStatus: z
    .union([z.nativeEnum(EEditionStatus), z.literal('missing')])
    .nullable(),
  verificationStatus: z.boolean().nullable()
});
type AdminAffidavitsGetEEditionTrackerDataRequest = z.infer<
  typeof AdminAffidavitsGetEEditionTrackerDataRequestSchema
>;

export const LedgerReceiptRequestSchema = z.object({
  ledgerItemId: z.string().min(20)
});

export const OrganizationsCreateRequestSchema = z.object({
  name: z.string().nonempty().max(MAX_ORGANIZATION_NAME_LENGTH),
  circulationSize: z.number().int().positive(),
  county: z.string().nonempty(),
  address: z.string().nonempty(),
  city: z.string().nonempty(),
  state: z.number().int().positive(),
  zipCode: z.string().nonempty(),
  phone: z.string().nonempty(),
  iana_timezone: z.enum([
    TimezoneType['America/Chicago'].logical,
    ...TimezoneType.items().map(item => item.logical)
  ])
});

export const ExperimentDraftCompletionRequestSchema = z.object({
  systemPrompt: z.string(),
  userInput: z.string()
});

const getDeadlineSchema = (dayEnum: number) =>
  z.object({
    dayEnum: z.number().int().min(dayEnum).max(dayEnum),
    publish: z.boolean(),
    deadline: z.object({
      dayEnum: z.number().int().min(1).max(7),
      time: z.string().regex(/^\d{2}:\d{2}$/)
    }),
    weeks: z.number().int().optional(),
    displayOffset: z.number().int().optional()
  });
export const AdminOrganizationsDeadlineSettingsRequestSchema = z.object({
  organizationId: z.string().min(20).max(20),
  deadlines: z.tuple([
    getDeadlineSchema(1),
    getDeadlineSchema(2),
    getDeadlineSchema(3),
    getDeadlineSchema(4),
    getDeadlineSchema(5),
    getDeadlineSchema(6),
    getDeadlineSchema(7)
  ])
});

export const AdminOrganizationsFuneralHomesConvertToFuneralHomeRequestSchema =
  z.object({
    orgId: z.string().min(10).max(40)
  });
type AdminOrganizationsFuneralHomesConvertToFuneralHomeRequest = z.infer<
  typeof AdminOrganizationsFuneralHomesConvertToFuneralHomeRequestSchema
>;

export const AdminOrdersTransferOwnershipRequestSchema = z.object({
  newUserId: z.string().min(10).max(40),
  orderId: z.string().min(10).max(40),
  newOrganizationId: z.string().min(10).max(40).nullable()
});
type AdminOrdersTransferOwnershipRequest = z.infer<
  typeof AdminOrdersTransferOwnershipRequestSchema
>;

export type AutoPaginateOrder = {
  variants: {
    width: number;
    height: number;
    columns: number;
  }[];
  order_type: string;
  id: string;
};

export const ListConversationRequestSchema = z.object({
  query: z.object({
    page: z.number().min(0).optional().default(0),
    limit: z.number().min(1).max(1000).optional().default(30),
    query: z.string().optional()
  }),
  tab: z.enum([
    'missing-placement',
    'missing-invoice',
    'missing-up-front-payment',
    'missing-billed-payment',
    'running',
    'missing-affidavit',
    'complete',
    'cancelled',
    'all'
  ])
});

export const AdminInboxAutomationTemplatesCreateRequestSchema = z.object({
  client: z.string(),
  state: z.string(),
  templateContent: z.string()
});
type AdminInboxAutomationTemplatesCreateRequest = z.infer<
  typeof AdminInboxAutomationTemplatesCreateRequestSchema
>;

export const AdminInboxAutomationTemplatesDeleteRequestSchema = z.object({
  client: z.string(),
  state: z.string()
});
type AdminInboxAutomationTemplatesDeleteRequest = z.infer<
  typeof AdminInboxAutomationTemplatesDeleteRequestSchema
>;

export const AdminInboxAutomationTemplatesPreviewRequestSchema = z.object({
  online: z.boolean(),
  multiTenant: z.boolean(),
  templateContent: z.string()
});
type AdminInboxAutomationTemplatesPreviewRequest = z.infer<
  typeof AdminInboxAutomationTemplatesPreviewRequestSchema
>;

type AdminRefundBulkPaidInvoiceRequest = {
  paymentIntentId: string;
  invoiceId: string;
};

type OrderRefundRequest = {
  orderId: string;
  newspaperOrderRefunds?: {
    newspaperOrderId: string;
    amountInCents: number;
  }[];
  refundReason: string | undefined;
};

export const StripeCustomerCreateRequestSchema = z.object({
  name: z.string(),
  email: z.string(),
  firestoreUID: z.string(),
  address: z
    .object({
      line1: z.string(),
      city: z.string(),
      state: z.string(),
      postal_code: z.string()
    })
    .or(z.object({}))
    .optional()
});
type StripeCustomerCreateRequest = z.infer<
  typeof StripeCustomerCreateRequestSchema
>;

export const CreateAllPublisherDocsRequestSchema = z.object({
  noticeId: z.string()
});
type CreateAllPublisherDocsRequest = z.infer<
  typeof CreateAllPublisherDocsRequestSchema
>;

export const CloudConvertRequestSchema = z.object({
  storageId: z.string(),
  input: z.string(),
  output: z.string(),
  opts: z.object({
    pages_fit_wide: z.number().optional()
  })
});
type CloudConvertRequest = z.infer<typeof CloudConvertRequestSchema>;

export const AdminIntegrationsResyncRequestSchema = z.object({
  triggerId: z.string().nonempty()
});
type AdminIntegrationsResyncRequest = z.infer<
  typeof AdminIntegrationsResyncRequestSchema
>;

const IntegrationSettingsSchema: z.ZodType<IntegrationSettings> = z.object({
  name: z.string().nonempty(),
  format: z.nativeEnum(IntegrationFormat),
  triggers: z.record(
    z.enum([
      ORDER_CANCELLED,
      ORDER_EDITED,
      ORDER_INVOICE_CREATED,
      ORDER_INVOICE_PAID,
      ORDER_INVOICE_PAID_OUT_OF_PLATFORM,
      ORDER_INVOICE_REFUNDED,
      ORDER_MANUAL_SYNC_REQUEST,
      ORDER_NEWSPAPER_ORDER_AT_DEADLINE,
      ORDER_NEWSPAPER_ORDER_CONFIRMED,
      ORDER_NEWSPAPER_ORDER_DELETED
    ]),
    z.boolean()
  )
});

export const AdminIntegrationsSettingsRequestSchema = z.object({
  editId: z.string().nonempty().nullable(),
  settings: IntegrationSettingsSchema
});
type AdminIntegrationsSettingsRequest = z.infer<
  typeof AdminIntegrationsSettingsRequestSchema
>;

/** `'none'` clears the export for that product. */
export const AdminOrderExportSettingsRequestSchema = z.object({
  organizationId: z.string().nonempty(),
  settings: z.object({
    [Product.Classified]: z.string().nonempty(),
    [Product.Obituary]: z.string().nonempty()
  })
});
type AdminOrderExportSettingsRequest = z.infer<
  typeof AdminOrderExportSettingsRequestSchema
>;

export const EmptyRequestSchema = z.object({});
type EmptyRequest = z.infer<typeof EmptyRequestSchema>;

/**
 * Request types for our API endpoints.
 */
export type ERequestTypes = {
  'admin/integrations/order-export-settings': AdminOrderExportSettingsRequest;
  'admin/briefs/run': { date?: string; cadence?: BriefBatchCadence };
  'admin/briefs/pdfs': {
    source: 'lee';
    date: string;
    /** Defaults to daily when absent. */
    cadence?: BriefBatchCadence;
    /**
     * Per-session identifier generated client-side. Every storagePath must
     * live under `legal-briefs-pdfs/{source}/{date}/{uploadId}/` (weekly:
     * `legal-briefs-pdfs/{source}/weekly/{date}/{uploadId}/`).
     */
    uploadId: string;
    files: Array<{
      filename: string;
      /**
       * Full GCS storage path the admin already PUT the PDF to via the
       * Firebase Storage SDK. Backend verifies existence + size before
       * finalizing the batch entries and event.
       */
      storagePath: string;
    }>;
  };
  'pagination/test-auto-pagination': {
    publicationIssueModelID: string;
    overrideExistingBlocks: boolean;
  };

  'admin/organizations/load-new-aging-data': {
    agingCsv: string;
  };
  'admin/organizations/set-report-url': {
    publisherId: string;
    reportUrl: string;
  };
  'admin/organizations/clone-publisher': {
    goodPublisherId: string;
    badPublisherId: string;
  };
  'admin/notices/display-site-notice/edit': {
    publicNoticeId: string;
    publicNotice: Partial<EPublicNotice>;
  };
  'admin/notices/edit-price-settings': {
    noticeId: string;
    requireUpfrontPayment: boolean;
  };
  'admin/notices/sync-to-public-notice-site': {
    noticeId: string;
  };
  'admin/organizations/layout-settings/update': {
    organizationId: string;
    noticeHeightThresholds: EOrganization['noticeHeightThresholds'] | null;
    grayscalePDFDisplayUploads: boolean;
  };
  'admin/organizations/bulk-refresh/notices': {
    organizationId: string;
  };
  'admin/display-site-uploads/generate-feed-key': {
    organizationId: string;
  };
  'admin/display-site-uploads/add-display-site-upload-id': {
    displaySiteCacheId: string;
    displaySiteUploadId: string;
    uploadIdData: {
      newspaperName: string;
      uploadID: string;
      newspaperId: string;
      counties: [string, ...string[]];
    };
  };
  'admin/display-site-uploads/delete-display-site-upload-id': {
    displaySiteCacheId: string;
    displaySiteUploadId: string;
  };
  'admin/display-site-uploads/update-display-site-upload-id': {
    displaySiteCacheId: string;
    displaySiteUploadId: string;
    displaySiteUploadIdUpdates: Partial<{
      newspaperName: string;
      uploadID: string;
      newspaperId: string;
      counties: [string, ...string[]];
    }>;
  };

  'admin/organizations/display-sites/update': {
    organizationId: string;
    displaySiteSettings: Partial<EOrganization['searchContext']>;
  };
  'admin/affidavits/organizations/:organizationId/update-edition-portal-url': {
    editionPortalURL: string;
  };
  'admin/notices/:noticeId/force-archive': {
    archiveReason: string;
  };
  'admin/notices/:noticeId/change-filer': AdminNoticesChangeFilerRequest;
  'admin/notices/:noticeId/cancel-invoice': AdminNoticesCancelInvoiceRequest;
  'search/publication-issue-sections': {
    filters: SearchablePublicationIssueSectionRecordFilter[];
    pageSize: number;
    pageNumber?: number;
    search: string;
    sort: Record<string, SORT_DIRECTIONS>[];
  };
  'docs/oauth': {};
  'conversation-requests/search': z.infer<typeof ListConversationRequestSchema>;
  'conversation-requests/refresh': { conversationId: string };
  'archive/parse-twp': { daysAgo: number };
  'admin/organizations/duplicate-publisher': {
    organizationId: string;
    newPublisherName: string;
    newPublisherCounty: string;
    newPublisherState: number;
  };
  'admin/customer-organizations/:customerOrganizationId/update': {
    name: string;
  };
  'admin/rates/:rateId/update': Partial<ERate>;

  'admin/organizations/refresh-affidavit-settings': {
    organizationId: string;
  };

  'admin/affidavits/scrape-edition-run-date': {
    runDate: string;
    publisherId: string;
    noticeId: string;
  };
  'admin/organizations/parent-org-settings': {
    organizationId: string;
    parentOrgId: string | null;
  };
  'admin/organizations/advertiser-express-settings': {
    organizationId: string;
    expressPlacementBilling: ExpressPlacementBilling | null;
  };
  'admin/organizations/express-settings': {
    organizationId: string;
    nonColumnPlacementSite: string;
    supportEmail: string;
    expressSettings: ExpressSettings;
    confirmedByEmail: string;
  };
  'admin/affidavits/notices/refresh-ars-from-publisher': {
    noticeId: string;
  };
  'users/update-role': UsersUpdateRoleRequest;
  'users/requests/accept': UsersAcceptJoinRequestRequest;
  'users/requests/decline': UsersDeclineJoinRequestRequest;
  'users/requests/snooze': UsersSnoozeJoinRequestRequest;
  'admin/orders/update-newspaper-order': {
    orderId: string;
    newspaperOrderId: string;
    status: NewspaperOrderStatus;
    layoutId: string;
    templateId: string | undefined;
  };
  'orders/download-rtf': { orderId: string };
  'admin/orders/update-ad-content': {
    orderId: string;
    adContent: string;
    adTitle: string | undefined;
    deceasedName: string | undefined;
    deceasedBirthDate: string | undefined;
    deceasedDeathDate: string | undefined;
    funeralHomeName: string | undefined;
  };
  'admin/orders/refresh-all-proofs': { publisherId: string };
  'admin/orders/refresh-proof': { orderId: string };
  'admin/orders/rebuild-newspaper-auth': { orderId: string };
  'admin/ad-building/orders/list-display-ads': {};
  'admin/ad-building/orders/group-ads-by-type': {
    newspaperOrderData: {
      newspaperId: string;
      newspaperOrderId: string;
      newspaperOrderText: string;
      filingTypeName: string;
      orderId: string;
    }[];
    filingTypeMapping: Record<string, string>;
  };
  'admin/ad-building/orders/download-block': {
    fileName: string;
    elements: any[];
    newspaperId: string;
    customElementUrls: { id: string; url: string }[];
  };
  'pagination/universal-download': {
    publisherId: string;
    date: string;
  };
  'payments/stripe-login-link': { accountId: string };
  'payments/stripe-payout-data': {};
  'payments/download-dwolla-payouts': {
    period: 'currMonth' | 'lastMonth' | 'currYear' | 'lastYear' | 'all';
    customerId: string;
  };
  'payments/dwolla-payout-data': {
    dwollaCustomer: string;
  };
  'payments/download-payouts': {
    period: 'currMonth' | 'lastMonth' | 'currYear' | 'lastYear' | 'all';
  };
  'payments/stripe-bt-payouts-data': {
    payouts: EStripePayout[];
    accountId: string;
  };
  'payments/create-receipt-pdf': { invoiceId: string };
  'admin/organizations/funeral-homes/convert-to-funeral-home': AdminOrganizationsFuneralHomesConvertToFuneralHomeRequest;
  'admin/orders/transfer-ownership': AdminOrdersTransferOwnershipRequest;
  'admin/display-qa': AdminDisplayQARequest;
  'admin/ad-building/download-block': AdminDownloadBlockRequest;
  'admin/affidavits/notices/:noticeId/force-assign-affidavit-to-notice': AffidavitAutomationForceAssignAffidavitToNoticeRequest;
  'admin/affidavits/notices/:noticeId/update-affidavit-reconciliation-settings': AdminNoticesUpdateAffidavitSettingsRequest;
  'admin/affidavits/get-runs-needing-verification': AffidavitAutomationGetRunsToVerifyRequest;
  'admin/affidavits/set-run-unverifiable': AffidavitAutomationSetUnverifiableRequest;
  'admin/affidavits/wet-signature/create-notarizations': AdminAffidavitsWetSignatureCreateNotarizationsRequest;
  'admin/affidavits/wet-signature/change-notarization-statuses': AdminAffidavitsWetSignatureChangeNotarizationStatusesRequest;
  'admin/automated-affidavit-signers': AutomatedAffidavitSignerRequestBody;
  'admin/automated-affidavit-signers/:signerId': AutomatedAffidavitSignerRequestBody;
  'admin/affidavits/get-eedition-tracker-data': AdminAffidavitsGetEEditionTrackerDataRequest;
  'admin/checks/:stripeCheckId/refresh': Record<string, never>;
  'admin/coupons/:couponId/delete': EmptyRequest;
  'admin/coupons/:couponId/update': CouponSaveRequest;
  'admin/coupons/create': CouponSaveRequest;
  'admin/external-upload': AdminExternalUploadRequest;
  'admin/impersonate-user': AdminImpersonateUserRequest;
  'admin/fonts/upload': AdminFontsUploadRequest;
  'admin/fonts/:fontName/delete': AdminFontsDeleteRequest;
  'admin/inbox-automation/templates/create': AdminInboxAutomationTemplatesCreateRequest;
  'admin/inbox-automation/templates/delete': AdminInboxAutomationTemplatesDeleteRequest;
  'admin/inbox-automation/templates/preview': AdminInboxAutomationTemplatesPreviewRequest;
  'admin/invoices/detach-invoice-from-notice': AdminInvoiceDetachRequest;
  'admin/invoices/void-detached-invoice': AdminInvoiceVoidDetachedRequest;
  'admin/invoices/regenerate-invoices': AdminInvoiceRegenerationRequest;
  'admin/invoices/refund-bulk-paid-invoice': AdminRefundBulkPaidInvoiceRequest;
  'admin/invoices/mark-uncollectible': { invoiceId: string };
  'admin/subscriptions/invoice-items-report': AdminSubscriptionsInvoiceItemsReportRequest;
  'admin/notices/:noticeId/upload-file': AdminNoticesUploadFileRequest;
  'admin/notices/:noticeId/replace-indd': AdminReplaceInddRequest;
  'admin/notices/:noticeId/upload-camera-ready-ad': AdminNoticesUploadCameraReadyAdRequest;
  'admin/notices/:noticeId/create-notice-transfer': Record<string, never>;
  'admin/orders/cancel': AdminOrderCancelRequest;
  'admin/orders/void-invoice': { invoiceId: string };
  'admin/orders/cancel-invoice': { invoiceId: string };
  'admin/organizations/caches/create': AdminOrganizationIntegrationCreateCache;
  'admin/organizations/caches/modify-description': AdminOrganizationIntegrationModifyCacheDescription;
  'admin/organizations/caches/modify-required': AdminOrganizationIntegrationModifyRequiredDescription;
  'admin/organizations/caches/delete-entry': AdminOrganizationIntegrationDeleteCacheEntry;
  'admin/organizations/caches/update-entry': AdminOrganizationIntegrationCacheEntry;
  'admin/organizations/export-settings': AdminOrganizationsIntegrationExportSettings;
  'admin/organizations/invoice-settings': AdminOrganizationsInvoiceSettings;
  'admin/organizations/plan-settings': AdminOrganizationsPlanSettingsRequest;
  'admin/organizations/product-implementation-settings': AdminOrganizationsProductSettingRequest;
  'admin/organizations/regenerate-affidavits': AdminOrganizationsRegenerateAffidavitsRequest;
  'admin/organizations/subdomain-settings': AdminOrganizationsSubdomainSettingsRequest;
  'admin/organizations/stripe-settings': AdminOrganizationsStripeSettingsRequest;
  'admin/organizations/upload-settings': AdminOrganizationsUploadSettings;
  'admin/organizations/integration-upload-settings': AdminOrganizationsIntegrationUploadSettings;
  'admin/organizations/build-export-settings': AdminOrganizationsBuildExportSettings;
  'admin/organizations/deadline-settings': z.infer<
    typeof AdminOrganizationsDeadlineSettingsRequestSchema
  >;
  'admin/payments/unreconciled-checks': z.infer<
    typeof AdminPaymentsUnreconciledChecksRequestSchema
  >;
  'admin/payments/reconciled-checks': AdminPaymentsReconciledChecksRequest;
  'admin/payments/lookup-stripe-event': z.infer<
    typeof AdminStripeEventRequestSchema
  >;
  'admin/payments/replay-stripe-event': z.infer<
    typeof AdminStripeEventRequestSchema
  >;
  'pagination/pagination-review': PaginationReviewNoticeRequest;
  'pagination/download-block': AdminDownloadBlockRequest;
  'pagination/download-pagination-issue-manifest': PaginationDownloadManifestRequest;
  'pagination/auto-paginate': {
    layouts: {
      id: string;
      width: number;
      height: number;
      max_whitespace?: number;
      min_whitespace?: number;
    }[];
    orders: AutoPaginateOrder[];
    hyperparams: OptimizerHyperparams;
  };
  'pagination/order-pagination-review': PaginationReviewOrderRequest;
  'pagination/pagination-download': PaginationDownloadRequest;
  'pagination/get-pagination-ready-orders': PaginationGetOrdersRequest;
  'admin/upload-customer-list': AdminUploadCustomerListRequest;
  'admin/verify-customer-list': AdminUploadCustomerListRequest;
  'admin/pagination-manifest': AdminPaginationManifestRequest;
  'admin/auto-pagination/test-auto-pagination': AdminAutoPaginationTestRequest;
  'admin/auto-pagination/update-auto-pagination-settings': AdminAutoPaginationUpdateSettingsRequest;
  'admin/auto-pagination/create-default-settings': AdminAutoPaginationCreateDefaultSettingsRequest;
  'admin/reps': AdminRepsRequest;
  'admin/paginators': AdminPaginatorsRequest;
  'admin/roles': AdminRoleRequest;
  'admin/sign-in': AdminSignInRequest;
  'admin/shadow': AdminShadowRequest;
  'admin/unshadow-an-org': AdminUnshadowOrgRequest;
  'admin/sync-to-elastic': AdminSyncToElasticRequest;
  'admin/regenerate-documents': AdminRegenerateDocumentsRequest;
  'admin/user-management/delete': AdminUserManagementDeleteRequest;
  'admin/user-management/organization-settings': AdminUserManagementOrganizationSettingsRequest;
  'admin/user-management/search': AdminUserManagementSearchRequest;
  'admin/user-management/update-stripe-id': AdminUserManagementStripeSettingsRequest;
  'admin/user-management/update-user-information': AdminUserManagementUserInformationRequest;
  'admin/user-management/update-notification-settings': AdminUserManagementUserNotificationsRequest;
  'admin/user-management/customer-io-messages': AdminUserManagementCustomerIOMessagesRequest;
  'affidavit-automation/events/create-eedition-uploaded-event': AffidavitAutomationCreateEEditionUploadedEventRequest;
  'affidavit-automation/notarization/add-to-notarization-batch': AffidavitAutomationNotarizationAddToNotarizationRequest;
  'affidavit-automation/notarization/:notarizationID/finalize': AffidavitAutomationFinalizeRequest;
  'affidavit-automation/notarization/override-notarization-preconditions': AffidavitAutomationOverrideNotarizationPreconditionsRequest;
  'affidavit-automation/notarization/get-notices-ready-for-wet-sig-notarization': AffidavitAutomationGetNoticesForWetSigNotarizationRequest;
  'affidavit-automation/notarization/assign-wet-sig-notices-to-mail-pages': AffidavitAutomationAssignWetSigNoticesToMailPagesRequest;
  'affidavit-automation/notarization/get-notices-assigned-to-mail-centers': AffidavitAutomationGetNoticesWithMailCenterAssignmentsRequest;
  'affidavit-automation/notarization/process-wet-sig-notarized-affidavit': AffidavitAutomationProcessWetSigNotarizedAffidavitRequest;
  'affidavit-automation/notarization/:notarizationID/process-complete': AffidavitAutomationNotarizationProcessCompleteRequest;
  'affidavit-automation/bulk-notarize': AffidavitAutomationBulkNotarizationRequest;
  'affidavit-automation/send-weekly-digest': AffidavitAutomationSendWeeklyDigestRequest;
  'affidavit-automation/manually-verify-runs': AffidavitAutomationManuallyVerifyRunsRequest;
  'affidavit-automation/get-all-notaries-from-proof': Record<string, unknown>;
  'bulk-export': AutomaticBulkDownloadRequest;
  'notices/async-design/upload-finalized-design': UploadFinalizedDesignRequest;
  'ftp/parse-filemage-path': FTPParseFilemagePathRequest;
  'display-ads/unapproved-display-ad-email-alert': DisplayAdsUnapprovedDisplayAdEmailAlertRequest;
  'display-ads/ax/:instance/orders/search': DisplayAdsAXOrdersSearchRequest;
  'documents/affidavits/preview': DocumentsAffidavitsPreviewRequest;
  'documents/generate-proof': GenerateProofRequest;
  'documents/pdf/download': PDFDownloadRequest;
  'documents/properties': DocumentsPropertiesRequest;
  'documents/zip': BulkDownloadRequest;
  'documents/invoices/download': InvoicesDownloadRequest;
  'documents/unpaid-invoices-summary/download': UnpaidInvoicesSummaryDownloadRequest;
  'documents/create-all-publisher-docs': CreateAllPublisherDocsRequest;
  'documents/cloud-convert': CloudConvertRequest;
  'drafts/:id/delete': DraftsDeleteRequest;
  'integrations/customers/:newspaperId/find': IntegrationsCustomersFindRequest;
  'integrations/async-design/notice/:noticeId/cancel-build-ad-request': IntegrationsCancelBuildAdRequest;
  'integrations/async-design/notice/:noticeId/create-build-ad-request': IntegrationsCreateBuildAdRequest;
  'integrations/trigger-event': IntegrationsTriggerEventRequest;
  'integrations/visionData/ocr': z.infer<
    typeof IntegrationsVisionDataOcrRequestSchema
  >;
  'integrations/visionData/response/:noticeId/:triggerId': VisionDataBotResponse;
  'invoice-transactions/create-authorization': InvoiceTransactionsCreateAuthorizationRequest;
  'invoice-transactions/create-charge': InvoiceTransactionsCreateChargeRequest;
  'invoice-transactions/create-session': InvoiceTransactionsCreateSessionRequest;
  'notices/create-invoice': Omit<EventInvoiceData, 'createdBy'>;
  'notices/:id/cancel': z.infer<typeof NoticesCancelRequestSchema>;
  'notices/request-cancellation': z.infer<
    typeof NoticesRequestCancellationRequestSchema
  >;
  'orders/apply-coupon': OrderCouponApply;
  'orders/remove-coupon': OrderCouponRemove;
  'orders/cancel': OrderCancelRequest;
  'orders/create': OrderCreateRequest;
  'orders/create-invoice': OrdersCreateInvoiceRequest;
  'orders/finalize-invoice': { invoiceId: string };
  'orders/download-proofs': OrdersDownloadProofsRequest;
  'orders/grant-access': OrdersGrantAccessRequest;
  'orders/update-price': { orderId: string; version: number };
  'orders/refund': OrderRefundRequest;
  'orders/get-font-resources': OrdersGetFontResourcesRequest;
  'orders/:orderId/submit-free': { version: number };
  'orders/:orderId/submit-unpaid': { version: number };
  'orders/:orderId/confirm-edits': OrdersConfirmEditsRequest;
  'orders/:orderId/delete': Record<string, never>;
  'payments/:id/refund': RefundInvoiceRequest;
  'payments/cancel-invoice': PaymentsCancelInvoiceRequest;
  'payments/create-bank-account': PaymentsCreateBankAccountRequest;
  'payments/delete-source': PaymentsDeleteSourceRequest;
  'payments/delete-payment-method': PaymentsDeletePaymentMethodRequest;
  'payments/download-payout': PaymentsDownloadPayoutRequest;
  'payments/stripe-cancel-setup-intent': PaymentsStripeCancelSetupIntentsRequest;
  'payments/list-invoice-payment-methods': PaymentsListInvoicePaymentMethodsRequest;
  'payments/list-customer-bank-accounts': PaymentsListCustomerBankAccountsRequest;
  'payments/pay-stripe-invoice': PaymentsPayInvoiceRequest;
  'payments/create-connected-account-link': PaymentsCreateConnectedAccountLinkRequest;
  'payments/mark-invoice-paid': PaymentsMarkInvoicePaidRequest;
  'payments/stripe-create-setup-intent': PaymentsStripeCreateSetupIntentRequest;
  'payments/get-bank-account-payment-method-status': PaymentsGetBankAccountPaymentMethodStatusRequest;
  'payments/attach-payment-method-to-customer': PaymentsAttachPaymentMethodToCustomerRequest;
  'payments/client-secret-for-setup-intent': PaymentsGetClientSecretForSetupIntentRequest;
  'payments/stripe-customer-details': PaymentsStripeCustomerDetailsRequest;
  'payments/generate-dwolla-payout': PaymentsGenerateDwollaPayoutRequest;
  'payway/queue-transaction': PaywayQueueTransactionRequest;
  'publication-issue/orders': GetPublicationIssueOrdersRequest;
  'search/pagination-table-rows': {
    orgIds: string[];
    startDate: string;
    endDate: string;
    sortByDeadline: ReportsPubIssuesTableDeadline;
    timezone: string;
  };
  'retool/organizations/create-advertiser-org': RetoolNewAdvertiserOrgRequest;
  'retool/organizations/:newspaperId/link-newspaper-parent': RetoolLinkNewspaperParentRequest;
  'retool/duplicate-newspaper': RetoolDuplicateNewspaperRequest;
  'search/invoices': SearchInvoicesRequest;
  'search/invoices-by-aging-aggregations': {
    orgIds: string[];
  };
  'search/unpaid-invoices': {
    orgIds: string[];
    params: { filerId?: string; filerOrgId?: string } | undefined;
  };
  'search/usernotices': SearchUserNoticesRequest;
  'search/usernotices/facets': SearchUserNoticesFacetRequest;
  'search/orders': SearchOrdersRequest;
  'search/orders/facets': SearchOrdersFacetsRequest;
  'search/organizations': SearchOrganizationsRequest;
  'search/public-notices': SearchPublicNoticesRequest;
  'search/public-notices/facets': SearchPublicNoticesFacetsRequest;
  'search/subdomains': SearchSubdomainsRequest;
  'search/customers': SearchCustomersRequest;
  'subscription/notices-subscription': CreatePublicNoticeFreeSubscriptionRequest;
  'subscription/paid-notices-subscription': CreatePublicNoticePaidSubscriptionRequest;
  'subscription/update-search-query': UpdateSubscriptionQueryRequest;
  'templates/:adTemplateId/archive': Record<string, unknown>;
  'templates/fork': TemplatesForkRequest;
  'templates/styles': TemplatesStylesRequest;
  'affidavit-automation/initiate-stak-verification': AffidavitAutomationStakInitiationRequest;
  'retool/add-upload-id': AddUploadIdRequest;
  'retool/organizations/set-prefer-customer-on-parent': SetPreferCustomerOnParentRequest;
  'site-settings': ProductSiteSettingCreateRequest;
  'site-settings/:siteSettingId': ProductSiteSettingUpdateRequest;
  'users/invite': UsersInviteRequest;
  'users/create-advertiser': UsersCreateAdvertiserRequest;
  'users/delete-invited-member': UsersDeleteInvitedMemberRequest;
  'users/delete-active-member': UsersDeleteActiveMemberRequest;
  'users/send-password-reset-email': UsersSendPasswordResetEmailRequest;
  'admin/user-management/migrate-notices-between-users': UsersMigrateNoticesBetweenAccountsRequest;
  'users/get-user-by-email': UsersGetUserByEmailRequest;
  'users/get-users-from-customer-org': UsersGetUsersFromCustomerOrgRequest;
  'users/register-organization': UsersRegisterOrganizationRequest;
  'users/register': UsersRegisterRequest;
  'users/change-email': UsersChangeEmailRequest;
  'users/send-change-account-email-link': UsersSendChangeAccountEmailLinkRequest;
  'users/sync-existing-notices': EmptyRequest;
  'auth/signin-exchange-token-for-google-microsoft-provider': GoogleMicrosoftSignInRequest;
  'search/advertiser-organizations': SearchAdvertiserOrganizationsRequest;
  'retool/notices/manual-sync': RetoolManualSyncRequest;
  'invoices/create-bulk-invoices': InvoicesCreateBulkInvoicesRequest;
  'invoices/create-bulk-invoices-loop': InvoicesCreateBulkInvoicesLoopRequest;
  'organization-management/merge': OrganizationManagementMergeRequest;
  'organization-management/delete': OrganizationManagementDeleteRequest;
  'organizations/related-papers': GetRelatedPapersRequest;
  'organizations/:organizationType/create': z.infer<
    typeof OrganizationsCreateRequestSchema
  >;
  'customers/get-or-create-with-customer-organization': GetOrCreateCustomerWithCustomerOrganizationRequest;
  'customers/get-or-create-customer': GetOrCreateCustomerRequest;
  'ad-pricing/price': AdPriceRequest;
  'image/upscale': { storagePath: string };
  'ledger/bulk-pay-invoices': z.infer<
    typeof LedgerBulkPayInvoicesRequestSchema
  >;
  'ledger/receipt': z.infer<typeof LedgerReceiptRequestSchema>;
  'search/adjudication-areas': SearchAdjudicationAreaRequest;
  'search/publishers/by-location': {
    county: string;
    state: string;
  };
  'search/notices-and-orders-by-month-aggregations': ColumnReportsBaseRequest;
  'search/product-category-aggregations': ColumnReportsBaseRequest;
  'search/customer-notices-and-orders-cohorts': ColumnReportsBaseRequest;
  'stripe/customer/create': StripeCustomerCreateRequest;
  'experiments/chat-complete': z.infer<
    typeof ExperimentDraftCompletionRequestSchema
  >;
  'admin/integrations/resync': AdminIntegrationsResyncRequest;
  'admin/integrations/settings': AdminIntegrationsSettingsRequest;
  'admin/api-clients/create': {
    name: string;
    slug: string;
    stripeCustomerId?: string | null;
    stripePaymentMethodId?: string | null;
    /** Required when stripePaymentMethodId is set, null otherwise */
    stripePaymentMethodType?: ApiClientPaymentMethodType | null;
    filerUserId?: string | null;
    filerOrganizationId?: string | null;
  };
  'admin/api-clients/update': {
    clientId: string;
    name?: string;
    stripeCustomerId?: string | null;
    stripePaymentMethodId?: string | null;
    /** Required when stripePaymentMethodId is set, null otherwise */
    stripePaymentMethodType?: ApiClientPaymentMethodType | null;
    filerUserId?: string | null;
    filerOrganizationId?: string | null;
  };
  'admin/api-clients/list': Record<string, never>;
  'admin/api-clients/revoke': {
    clientId: string;
  };
};

type RequestKeys = keyof ERequestTypes;
type ResponseKeys = keyof EResponseTypes;
export type ApiEndpoint = RequestKeys & ResponseKeys;
export type ApiGetEndpoint = ResponseKeys;
